import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useParams, Link, useNavigate, useSearchParams } from 'react-router-dom';
import useAutoRefresh from '../hooks/useAutoRefresh';
import useSessionState, { clearSessionState } from '../hooks/useSessionState';
import {
  Save, X, Plus, Trash2, Edit, Upload, Download,
  AlertCircle, CheckCircle, Eye, EyeOff, ChevronDown, ChevronUp,
  Play, RefreshCw, FileText, Copy, Trash, Search, SkipForward, FileSearch, ChevronRight,
  Code
} from 'lucide-react';
import syncService from '../services/syncService';
import { config } from '../config';
import { useAuth } from '../contexts/AuthContext';
import { useAccount } from '../contexts/AccountContext';
import Card from './shared/Card';
import Button from './shared/Button';
import Spinner from './shared/Spinner';
import CredentialsPicker from './shared/CredentialsPicker';
import CredentialChip from './shared/CredentialChip';
import CredentialSelector from './shared/CredentialSelector';
import CopyConversionsModal from './shared/CopyConversionsModal';
import { ConversionEditModal, ConversionCSVUploadModal } from './shared/ConversionModals';
import ApiKeyField from './shared/ApiKeyField';
import CopySettingsModal from './shared/CopySettingsModal';
import ConfirmModal from './shared/ConfirmModal';
import TaskSummaryStats from './shared/TaskSummaryStats';
import ReprocessProfilesModal from './shared/ReprocessProfilesModal';
import { ExchangeViewerOverlay } from './shared/ExchangeViewer';
import useErrorKb from './shared/useErrorKb';
import { classifyStoryLine, commentWithAnnotations, hiddenHistoryMatches } from './shared/messageLogUtils';
import LinkifiedText from './shared/LinkifiedText';
import { orderedTasksForTab } from './shared/taskOrder';
import { REQUEST_SCOPE_OPTIONS } from './RecordEditModal';
import { parseMissingProfiles } from '../utils/missingProfiles';
import { formatDateTime } from '../utils/datetime';
import ConversionCodesPanel from './shared/ConversionCodesPanel';
import StayUnmappedCodesPanel from './shared/StayUnmappedCodesPanel';
import ProcessRunReport from './shared/ProcessRunReport';
import './TaskConfigurationPage.css';
import './TaskReconciliationPage.css';
import './PaymentPorterPage.css'; // pp-toggle-btn styles for the auto-refresh toggle
import OxiTopologyDiagram from './OxiTopologyDiagram';
import { getRecordStatusColor, getRecordStatusTitle, RECORD_STATUS_OPTIONS } from '../utils/recordStatus';

// Sync connector API base URL (custom domain), taken from the "Sync" DNS record. Used to build the
// example `GET /{apiKey}/check` request and the OXI inbound URL shown for OXI tasks (see the API Key
// section below).
const SYNC_API_BASE = (process.env.REACT_APP_DNS_RECORDS || '')
  .split(',').map(s => s.trim()).filter(Boolean)
  .map(e => e.split('|').map(x => x.trim()))
  .filter(pair => (pair[0] || '').toLowerCase() === 'sync')
  .map(pair => pair[1])[0] || '';

const SYNC_HOST = SYNC_API_BASE.replace(/\/$/, '');

// Gates the connection-path diagram, which describes `/oxi/{account}` on the connectors API
// (terraform/connectors-apigateway.tf -> connectors-oxi-dequeue / -enqueue), where {account} is this
// task's API key. It only makes sense for an OXI source, so it checks the connector's marketplace system
// name as well as the API-key flag — today OPERA OXI is the only marketplace with USES_API_KEY='Y', but
// that is a data value someone can change, and showing an /oxi/ path for a non-OXI connector would be wrong.
const isOxiConnector = (task) =>
  task?.usesApiKey === 'Y' && /OXI/i.test(String(task?.connectorSystemName || ''));

// A copyable API-key URL, shown in full so it can be read straight off the screen. `prefix` carries what
// the value is for — the verification block is a command to run, so it passes `curl `. Display and copy
// always use the same string.
const ApiKeyUrlBlock = ({ label, hint, apiKey, buildUrl, prefix = '', onCopy, copyMessage }) => {
  const value = (key) => `${prefix}${buildUrl(key)}`;
  return (
    <div style={{ marginTop: '10px' }}>
      {label && <label className="form-label">{label}</label>}
      <div style={{ display: 'flex', gap: '8px', alignItems: 'stretch' }}>
        <code style={{
          flex: 1, fontFamily: 'monospace', fontSize: '0.85rem', background: '#f3f4f6',
          border: '1px solid #e5e7eb', borderRadius: '6px', padding: '9px 11px',
          overflowX: 'auto', whiteSpace: 'nowrap', color: '#111827',
        }}>
          {value(apiKey || '<api key>')}
        </code>
        <button
          type="button"
          className="btn-secondary"
          onClick={() => { if (apiKey) onCopy(value(apiKey), copyMessage); }}
          title="Copy"
          style={{ marginLeft: 0 }}
          disabled={!apiKey}
        >
          <Copy size={16} />
        </button>
      </div>
      <p className="form-hint">{hint}</p>
    </div>
  );
};



// PEP file records used to carry a no-login short link in their comment ("Download (expires …): …/shorturl/…").
// Those links are revoked (V531) and no longer written; the sentence is hidden so nobody is offered a dead
// link — the record's "Link to file" replaces it.
const withoutShortLink = (text) => (text == null ? text : String(text)
  .replace(/\s*Download \(expires [^)]*\):\s*https?:\/\/\S+\/shorturl\/\S+/g, '')
  .trim());

function TaskConfigurationPage() {
  const { syncId, taskId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const fromTab = searchParams.get('from') || 'configuration';
  const { user } = useAuth();
  const isSuperAdmin = !!user?.isSuperAdmin;
  const { selectedAccount } = useAccount();

  // Main data state
  const [task, setTask] = useState(null);
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  // Settings state
  const [formSettings, setFormSettings] = useState({});
  const [validationErrors, setValidationErrors] = useState({});
  const [showPasswords, setShowPasswords] = useState({});
  const [expandedGroups, setExpandedGroups] = useState({});

  // Conversions state
  const [conversions, setConversions] = useState([]);
  // THE CODES THIS TASK LOADED FROM THE PROPERTY (V493). The list the source system answered with — every
  // transaction code it actually uses — shown as this task's records in place of a file summary.
  const [conversionDataTypes, setConversionDataTypes] = useState([]);
  const [loadingConversions, setLoadingConversions] = useState(false);
  const [filterRecordType, setFilterRecordType] = useState('');
  const [filterField, setFilterField] = useState('');
  const [filterSearch, setFilterSearch] = useState('');
  const [showConversionModal, setShowConversionModal] = useState(false);
  const [editingConversion, setEditingConversion] = useState(null);
  const [showCSVUploadModal, setShowCSVUploadModal] = useState(false);
  const [showCopyConversionsModal, setShowCopyConversionsModal] = useState(false);
  const [showCopySettingsModal, setShowCopySettingsModal] = useState(false);
  const [copyingConversions, setCopyingConversions] = useState(false);

  // Task summary state
  const [taskSummary, setTaskSummary] = useState([]);
  const [loadingSummary, setLoadingSummary] = useState(false);

  // In-flight message counts by scope
  const [inflightCounts, setInflightCounts] = useState({});
  const [purgingRecords, setPurgingRecords] = useState(false);
  const [showPurgeConfirm, setShowPurgeConfirm] = useState(false);
  const [showReportsModal, setShowReportsModal] = useState(false); // "Reports" picker (Overview, On-site instructions, …)

  // Actions state
  const [executingAction, setExecutingAction] = useState(null);
  const [showActionConfirm, setShowActionConfirm] = useState(null);

  // Send instruction state
  const [sendingInstruction, setSendingInstruction] = useState(false);

  // Invoke process state
  const [invokingProcess, setInvokingProcess] = useState(false);
  // Import Offline Extract: the .hcx being uploaded, and how far it has got (null when idle).
  const [offlineUploadPct, setOfflineUploadPct] = useState(null);
  // Import Offline Extract: the hub's verdict on the package just uploaded (V633 build check + the import's checks) —
  // { status: 'CHECKING' | 'ACCEPTED' | 'REFUSED' | 'ERROR' | 'TIMEOUT', message, fileName }, null when there is none.
  const [offlineCheck, setOfflineCheck] = useState(null);
  const offlinePackageInput = useRef(null);

  // Communication status state
  const [communicationStatus, setCommunicationStatus] = useState(null);
  const [testingConnection, setTestingConnection] = useState(false);

  // Toast state
  const [toast, setToast] = useState(null);

  // Error Knowledge Base — shared hook (entries keyed by normalised error signature + trigger/modal).
  const { renderKbTriggerFor, kbModalElement } = useErrorKb(task?.syncTemplateId, isSuperAdmin);
  // Reconciliation rows expanded to show their status history + source data points.

  // Reconciliation state
  const reconciliationRef = useRef(null);
  const reconRequestIdRef = useRef(0);
  // The search the spinner belongs to (0 = none). The 3s auto-refresh used to overtake a slow search: it took a newer
  // request id, so the search's answer was dropped AND its finally could not clear the spinner — and a silent poll never
  // clears it — so the button sat on "Searching…" for good. Now a poll stands aside while a search runs, and the
  // spinner is cleared by whichever search set it last.
  const reconLoadingIdRef = useRef(0);
  const [reconRecords, setReconRecords] = useState([]);
  const [reconLoading, setReconLoading] = useState(false);
  const [reconLoadingMore, setReconLoadingMore] = useState(false);
  const [reconDownloading, setReconDownloading] = useState(false);
  const [reconError, setReconError] = useState(null);
  // Persisted per task: opening a record's message log unmounts this page, and without this the
  // filters and the "have I searched" flag reset, so returning showed "Ready to Search" and an empty
  // table. Criteria only — the records themselves are re-fetched on mount (see the restore effect).
  const reconStateKey = `recon:${taskId}`;

  // ── SWITCHING TASKS CLEARS THE FILTER ROW (HIL-88) ─────────────────────────────────────────────
  //
  // The two things this state has to do pull in opposite directions. Opening a record's message log
  // unmounts this page, and coming back must not drop your filters — that is why they are in
  // sessionStorage at all. But the state is keyed per task, so stepping to Profiles and back to
  // Reservations ALSO restored them, and the reporter arrived at a task showing Status "Error" and a
  // stale search term they had set minutes earlier on a different screen. A filter you did not choose,
  // silently hiding rows, is worse than one you have to retype.
  //
  // Both are satisfied by asking WHERE YOU CAME FROM rather than how long ago. One marker records which
  // task the stored criteria belong to. Return to that task from a record log and the marker still
  // matches, so nothing is touched. Arrive from a different task and the incoming task's saved
  // criteria are dropped before they are read.
  //
  // DURING RENDER, NOT IN AN EFFECT. useSessionState re-reads storage in a key-change effect, and
  // effects run after render — clearing in an effect of our own would race it and sometimes lose. A
  // useMemo runs first, so the hooks below read a key that is already gone and fall back to their
  // defaults.
  //
  // The marker deliberately sits outside the `recon:<task>:` namespace so clearing a task's criteria
  // cannot delete the record of who owns them.
  useMemo(() => {
    const OWNER_KEY = 'recon:ownerTask';
    try {
      if (sessionStorage.getItem(OWNER_KEY) !== String(taskId)) {
        clearSessionState(`${reconStateKey}:`);
        sessionStorage.setItem(OWNER_KEY, String(taskId));
      }
    } catch (e) {
      // Storage disabled or full: the filters simply do not persist, which is the safe direction.
    }
  }, [taskId, reconStateKey]);

  const [reconHasSearched, setReconHasSearched] = useSessionState(`${reconStateKey}:hasSearched`, false);
  // ONE auto-refresh for the merged Summary + Reconciliation card: the counts and the records are
  // two views of the same data, so refreshing them on separate timers showed a total that
  // disagreed with the rows underneath it for up to two seconds at a time.
  // Auto-refresh, on by default (HIL-87). The hook owns the interval, the tab-hidden pause and the
  // remembered off-switch — see useAutoRefresh.

  const [processRefreshing, setProcessRefreshing] = useState(false);  // manual Process Run refresh in flight
  const [reqSourceId, setReqSourceId] = useState('');       // superadmin "request by source id" input
  const [reqScope, setReqScope] = useState('');
  const [reqSubmitting, setReqSubmitting] = useState(false);
  const [showBulkRequestModal, setShowBulkRequestModal] = useState(false); // superadmin "request by source id from file"
  // The request controls used to sit inline under the filters, where a four-control panel pushed the
  // record list further down the page for a superadmin-only action used occasionally.
  const [showRequestModal, setShowRequestModal] = useState(false);
  const [reconNextContinuationToken, setReconNextContinuationToken] = useState(null);
  const [reconScopeType, setReconScopeType] = useSessionState(`${reconStateKey}:scopeType`, '');
  const [reconRecordStatus, setReconRecordStatus] = useSessionState(`${reconStateKey}:recordStatus`, '');
  // ONE search box across source id / target id / comment (the backend ORs them — see
  // searchSprRecords). Three separate fields meant you had to know WHICH id you were holding before
  // you could look it up, and pasting a value into the wrong one returned "no records" rather than
  // the record.
  const [reconSearch, setReconSearch] = useSessionState(`${reconStateKey}:search`, '');
  const [reconShowOnlyNullTargetId, setReconShowOnlyNullTargetId] = useSessionState(`${reconStateKey}:onlyNullTargetId`, false);
  const [reconSelectedRecords, setReconSelectedRecords] = useState(new Set());
  const [reconPage, setReconPage] = useSessionState(`${reconStateKey}:page`, 1);
  const reconPageSize = 25;
  const [reconSortBy, setReconSortBy] = useSessionState(`${reconStateKey}:sortBy`, 'lastSyncTime');
  const [reconSortOrder, setReconSortOrder] = useSessionState(`${reconStateKey}:sortOrder`, 'desc');
  const [reconProcessingBulkAction, setReconProcessingBulkAction] = useState(false);
  const [reconShowConfirmModal, setReconShowConfirmModal] = useState(false);

  // THE FAILING REQUEST, FOR PEOPLE WHO CANNOT OPEN THE MESSAGE LOG.
  //
  // The message-log button on a reconciliation row is superadmin-only, so a property user reads
  // "PUT …/allocation → 400 BLK00050: No rooms available at ROOM TYPE level" and has no way to see the
  // allocation that was actually sent — which is the thing they can fix and we cannot. This holds that
  // one exchange and nothing else; the server does the filtering, so the rest of the transcript never
  // reaches the browser.
  const [exchangeView, setExchangeView] = useState(null); // { record, exchange, loading, error }
  const [reconProfileWizardItems, setReconProfileWizardItems] = useState(null); // [{record, missingProfiles}] or null
  const [showSingleFileConfirm, setShowSingleFileConfirm] = useState(false);
  const [showProcessConfirm, setShowProcessConfirm] = useState(false);
  const [showProcessSettings, setShowProcessSettings] = useState(false);
  const [processSettingValues, setProcessSettingValues] = useState({});
  const [processSettingsError, setProcessSettingsError] = useState(null);
  const [lastProcessValues, setLastProcessValues] = useState(null); // last-submitted step-setting values (for one-click reprocess)
  const [reconPendingAction, setReconPendingAction] = useState(null);
  const [reconBulkActionResult, setReconBulkActionResult] = useState(null);
  const [reconShowSuccessModal, setReconShowSuccessModal] = useState(false);
  const [reconShowDeleteModal, setReconShowDeleteModal] = useState(false);
  const [reconRecordsToDelete, setReconRecordsToDelete] = useState([]);
  const [reconDeleting, setReconDeleting] = useState(false);
  const [reconShowCSVUploadModal, setReconShowCSVUploadModal] = useState(false);

  // CONNECTIVITY settings tab (AUTH / CONFIG)
  const [settingsTab, setSettingsTab] = useState('AUTH');

  // Shared credentials (now driven by the unified CredentialsPicker)
  const [sharedCredentials, setSharedCredentials] = useState([]);                   // legacy — read by group-overrides logic
  const [selectedSharedMarketplace, setSelectedSharedMarketplace] = useState(null); // marketplaceId of the selected credential
  const [selectedSharedCred, setSelectedSharedCred] = useState(null);               // {entity_type, entity_id, credential_name}
  const [selectedShareId, setSelectedShareId] = useState(null);                     // entity_credential_shares.id to bind to connector
  const [showCredentialPicker, setShowCredentialPicker] = useState(false);          // full-screen picker view
  const originalFormSettings = useRef({});

  // Apply a picked credential: pre-fill the form with its values and stash the share id for save.
  const applySelectedCredential = (cred) => {
    setSelectedSharedCred(cred);
    setSelectedSharedMarketplace(cred.marketplace_id);
    const newValues = { ...originalFormSettings.current };
    (cred.settings || []).forEach(s => {
      const value = s.must_be_overridden === 'Y' ? '' : (s.setting_value || '');
      const matchKey = Object.keys(newValues).find(k => k.toLowerCase() === s.setting_name.toLowerCase());
      if (matchKey !== undefined) newValues[matchKey] = value;
    });
    setFormSettings(newValues);
    setSelectedShareId(cred.share_id || null);
    setShowCredentialPicker(false);
  };

  // Clear the selected credential (Remove). The connector binding is cleared on next save.
  const handleRemoveCredential = () => {
    setSelectedSharedCred(null);
    setSelectedSharedMarketplace(null);
    setSelectedShareId(null);
  };

  // API key field (for marketplaces with USES_API_KEY = 'Y')
  const [apiKey, setApiKey] = useState('');

  useEffect(() => {
    if (syncId && taskId) {
      loadTaskData();
    }
  }, [syncId, taskId]);

  // The tasks of the tab the user came from (extraction tasks for from=tasks, process tasks for
  // from=process, …) — the same ordered list the project detail page renders, which is what the task
  // switcher offers. prev/next were dropped with the Previous/Next buttons.
  // WHICH TASK THE URL NAMES, whichever identifier it carries.
  //
  // The address bar holds the numeric syncprojecttaskid now, but a link made before that change — a
  // bookmark, or one pasted into a ticket — still carries the uuid. Matching on one alone means the
  // switcher silently disappears on every link of the other kind, because `index` comes back as -1 and
  // the control is hidden rather than showing an error.
  const taskKey = (t) => String(t.syncProjectTaskId ?? t.syncProjectTaskUuid ?? '');
  const isCurrentTask = (t) => taskKey(t) === String(taskId)
    || String(t.syncProjectTaskUuid ?? '') === String(taskId);

  const taskNav = useMemo(() => {
    const ordered = orderedTasksForTab(project?.projectTasks, project?.taskGroups, fromTab);
    const idx = ordered.findIndex(isCurrentTask);
    return { ordered, index: idx, total: ordered.length };
    // isCurrentTask is a plain comparison over its arguments, so it holds no state worth tracking here.
  }, [project, taskId, fromTab]);

  // Addressed by id, like the project. The uuid is still accepted by the API, so a link made before
  // this change keeps working; new ones are readable and quotable.
  const goToTask = (t) => {
    if (t) navigate(`/sync/${syncId}/task/${t.syncProjectTaskId || t.syncProjectTaskUuid}/configure?from=${fromTab}`);
  };

  const loadTaskData = async () => {
    let taskData;
    try {
      setLoading(true);
      setError(null);

      // Load task details
      taskData = await syncService.getTaskDetails(syncId, taskId);
      setTask(taskData);

      // Load project details
      const projectData = await syncService.getSyncProjectDetails(syncId);
      setProject(projectData);

      // Initialize settings form
      if (taskData.taskSettings && Array.isArray(taskData.taskSettings)) {
        const initialSettings = {};
        const initialExpanded = {};

        taskData.taskSettings.forEach(setting => {
          initialSettings[setting.settingName] = setting.settingValue || '';
          // Expand every group by default (both settingGroup — AUTH tab — and SUB_GROUP / "Other" —
          // CONFIG tab) so no section is hidden on load.
          initialExpanded[setting.settingGroup || 'General'] = true;
          initialExpanded[(setting.subGroup && String(setting.subGroup).trim()) || 'Other'] = true;
        });

        setFormSettings(initialSettings);
        originalFormSettings.current = initialSettings;
        setExpandedGroups(initialExpanded);
      }

      if (taskData.usesApiKey === 'Y' && taskData.apiKey) {
        setApiKey(taskData.apiKey);
      }

      if (taskData.communicationStatus) {
        setCommunicationStatus(taskData.communicationStatus);
      }

      // Load conversions if applicable (the Default Values task shares the same store + dropdowns)
      if (shouldShowConversions(taskData.taskName) || shouldShowDefaultValues(taskData.taskName)) {
        if (Array.isArray(taskData.conversionDataTypes)) {
          setConversionDataTypes(taskData.conversionDataTypes);
        }
        await loadConversions();
      }

      // Load task summary if applicable
      if (shouldShowSummary(taskData.taskName)) {
        await loadTaskSummary();
      }

    } catch (err) {
      console.error('Error loading task data:', err);
      setError(err.response?.data?.message || err.message || 'Failed to load task configuration');
    } finally {
      setLoading(false);
    }

    // Restore the inherited credential binding from the authoritative task-details payload.
    // The binding is keyed by share id (entity_credential_shares.id) and the backend resolves the
    // credential's display info by that id — we trust it directly rather than re-deriving the share
    // from the picker, which could return a different (more-specific) share row and silently drop
    // the binding, exposing every inherited AUTH value as an editable field.
    if (taskData?.inheritedCredential) {
      const inh = taskData.inheritedCredential;
      setSelectedSharedCred({
        share_id: inh.shareId,
        credential_id: inh.credentialId,
        credential_name: inh.credentialName,
        entity_type: inh.entityType,
        entity_id: inh.entityId,
        marketplace_id: inh.marketplaceId,
        settings: inh.settings || [],
      });
      setSelectedSharedMarketplace(inh.marketplaceId);
      setSelectedShareId(inh.shareId);
    } else if (taskData?.inheritedCredentialShareId) {
      // Binding exists but its display info couldn't be resolved — keep it active so inherited
      // AUTH fields stay hidden (and a save won't write them back as raw settings).
      setSelectedShareId(taskData.inheritedCredentialShareId);
      setSelectedSharedCred({ share_id: taskData.inheritedCredentialShareId, credential_name: 'Linked credential', settings: [] });
    }
  };

  const handleSelectSharedCredential = (cred) => {
    setSelectedSharedMarketplace(cred.marketplace_id);
    const newValues = { ...originalFormSettings.current };
    cred.settings.forEach(s => {
      const value = s.must_be_overridden === 'Y' ? (s.override_value || '') : (s.setting_value || '');
      const matchKey = Object.keys(newValues).find(k => k.toLowerCase() === s.setting_name.toLowerCase());
      if (matchKey !== undefined) newValues[matchKey] = value;
    });
    setFormSettings(newValues);
  };

  const handleSelectManualEntry = () => {
    setSelectedSharedMarketplace(null);
    setSelectedSharedCred(null);
    setSelectedShareId(null);
    // Clear all fields — shared credential values must not be visible when entering manually
    const cleared = {};
    Object.keys(originalFormSettings.current).forEach(k => { cleared[k] = ''; });
    setFormSettings(cleared);
  };

  const loadConversions = async () => {
    try {
      setLoadingConversions(true);
      const conversionsData = await syncService.getConversions(syncId, taskId);
      setConversions(Array.isArray(conversionsData) ? conversionsData : []);
    } catch (err) {
      console.error('Error loading conversions:', err);
      showToast('Failed to load conversions', 'error');
    } finally {
      setLoadingConversions(false);
    }
  };

  // `selections` comes from the wizard: only the conversions the other project has that this one does
  // not, and only the ones the user ticked. The count in the toast is what the SERVER wrote, not what
  // was sent — a row someone else added between opening the wizard and pressing Copy is skipped, and
  // saying "12 copied" when 9 landed is the kind of small lie that costs an afternoon later.
  const handleCopyConversions = async (sourceProject, selections) => {
    setCopyingConversions(true);
    try {
      // `syncId` is what GET /sync returns (db.getSyncProjects: `sp.syncid AS syncId`). This used to
      // read `syncProjectId || syncid` — neither of which that endpoint has ever produced — so the id
      // was undefined, JSON.stringify dropped the key, and every copy came back
      // 400 "sourceProjectId is required". The other spellings are kept as fallbacks because the same
      // project shape is built by more than one query.
      const sourceProjectId = sourceProject.syncId || sourceProject.syncid
        || sourceProject.syncProjectId || sourceProject.sync_project_id;
      if (!sourceProjectId) {
        showToast(`Could not identify "${sourceProject.projectName}" — nothing was copied`, 'error');
        return;
      }
      const result = await syncService.mergeConversionsFromProject(
        syncId, taskId, sourceProjectId, selections);
      setShowCopyConversionsModal(false);
      await loadConversions();
      const merged = result.merged || 0;
      const skipped = result.skipped || 0;
      const noun = isDefaultsTask
        ? (merged === 1 ? 'default value' : 'default values')
        : (merged === 1 ? 'conversion' : 'conversions');
      showToast(
        `${merged} ${noun} copied from "${sourceProject.projectName}"`
        + (skipped ? ` — ${skipped} already existed and ${skipped !== 1 ? 'were' : 'was'} left alone` : ''),
        'success');
    } catch (err) {
      showToast(err.response?.data?.message || 'Failed to copy conversions', 'error');
    } finally {
      setCopyingConversions(false);
    }
  };

  // Copy settings writes into the FORM, not to the server — the user still has to press Save. That is
  // deliberate: the save path carries the role, the api key and the inherited credential alongside the
  // values, and a modal that posted on its own would be a second version of it to keep in step. It also
  // means the copied values can be looked at, and edited, before they become this connector's config.
  const handleCopySettings = (picked, sourceProject) => {
    const names = Object.keys(picked);
    if (!names.length) { setShowCopySettingsModal(false); return; }
    setFormSettings(prev => ({ ...prev, ...picked }));
    setShowCopySettingsModal(false);
    showToast(
      `${names.length} setting${names.length !== 1 ? 's' : ''} copied from "${sourceProject.projectName}" — `
      + 'review them and press Save to apply',
      'success');
  };

  const loadInflight = async () => {
    try {
      const data = await syncService.getInflight(syncId);
      const map = {};
      (Array.isArray(data) ? data : []).forEach(r => { map[r.scopeType] = r.inflight; });
      setInflightCounts(map);
    } catch (err) {
      console.error('Error loading in-flight counts:', err);
    }
  };

  // Render a "story" string (one call per \n-separated line) as separated, outcome-coloured steps, each
  // with its own KB trigger. Shared by the reconciliation comment cell and the message-log exit view.
  const renderStoryLines = (text) => {
    if (!text || !String(text).trim()) return null;
    const steps = String(text)
      .split('\n')
      .map(l => l.replace(/\s+$/, ''))
      .filter(l => l.trim() !== '')
      // IP PROTECTION. A green line is a call that SUCCEEDED — together they spell out the exact
      // sequence of requests we make against the target, which is our implementation rather than the
      // customer's data. Errors and warnings stay: those are the customer's to act on, and a warning is
      // often the only explanation for why a record looks odd. Notes (the neutral lines, e.g. the
      // account-97 rule trace) stay too — they describe what happened to THEIR data.
      .filter(l => isSuperAdmin || classifyStoryLine(l) !== 'success');
    if (steps.length === 0) return null;
    return (
      <div className="story-steps">
        {steps.map((line, i) => (
          <div key={i} className={`story-step story-step-${classifyStoryLine(line)}`}>
            <span className="story-step-text" title={line}>
              <LinkifiedText text={line} />
            </span>
            {renderKbTriggerFor(line)}
          </div>
        ))}
      </div>
    );
  };

  // The comment PLUS the record's status_history annotations. oracle-ohip's terminal stamp replaces the
  // comment wholesale, so "[cc] all token(s) swapped" never survives into it — rendering the comment
  // alone left the swap result on a record that visibly showed everything else about the run.
  // The comment, plus — when a search is running — the earlier history line that the search actually
  // matched on (HIL-89). Without it a row answers a search for "missing" while showing no such word,
  // and the reader has no way to tell a real hit from a broken filter.
  const renderStoryComment = (record) => {
    const story = renderStoryLines(withoutShortLink(commentWithAnnotations(record)));
    const matched = hiddenHistoryMatches(record, reconSearch);
    if (!story && !matched.length) return <span className="empty-value">-</span>;
    return (
      <>
        {story}
        {matched.map((line, i) => (
          <div className="recon-matched-history" key={i} title="An earlier comment on this record — this is what your search matched">
            <span className="recon-matched-history-label">Earlier</span>
            {line}
          </div>
        ))}
      </>
    );
  };

  // `silent` polls without swapping the card for the loading spinner and without toasting on failure —
  // at a 3s cadence a spinner would flicker continuously and a transient error would spam the screen.
  // The last good numbers stay on display until the next successful poll.
  const loadTaskSummary = async ({ silent = false } = {}) => {
    try {
      if (!silent) setLoadingSummary(true);
      const summaryData = await syncService.getTaskSummary(syncId, taskId);
      const summary = Array.isArray(summaryData) ? summaryData : [];
      setTaskSummary(summary);
      if (summary.length === 1) setReconScopeType(summary[0].scopeType);
    } catch (err) {
      console.error('Error loading task summary:', err);
      if (!silent) showToast('Failed to load task summary', 'error');
    } finally {
      if (!silent) setLoadingSummary(false);
    }
  };

  const handlePurgeRecords = async () => {
    try {
      setPurgingRecords(true);
      setShowPurgeConfirm(false);
      const result = await syncService.purgeTaskRecords(syncId, taskId);
      showToast(`Purged ${result.deleted} record${result.deleted !== 1 ? 's' : ''}`, 'success');
      await loadTaskSummary();
    } catch (err) {
      console.error('Error purging records:', err);
      showToast(err.response?.data?.message || 'Failed to purge records', 'error');
    } finally {
      setPurgingRecords(false);
    }
  };

  const isConversionCodeTask = task?.isExcelConversionCodeTask === 'Y';

  const shouldShowConversions = (taskName) => {
    return taskName === 'CONVERSIONS';
  };
  // The Default Values task reuses the entire Conversions UI + store — a "default" is just a conversion
  // row with an EMPTY source value (a target value with no source). Only the Source field is hidden and
  // the list is filtered to empty-source rows; everything else (record-type + field dropdowns, CRUD,
  // CSV, copy-from) is shared.
  const shouldShowDefaultValues = (taskName) => taskName === 'DEFAULTVALUES';
  const isDefaultsTask = shouldShowDefaultValues(task?.taskName);

  const isProcessOrScheduleTask = () => ['PROCESS', 'SCHEDULE'].includes(task?.taskGroup?.toUpperCase());

  const shouldShowSummary = (taskName) => {
    if (isProcessOrScheduleTask()) return false;
    // A CONFIG-phase task (connector source/target setup, conversions, etc.) is editable config, never a
    // records summary. Keying on the phase/group covers connectors whose config task isn't named
    // "CONFIGURE SOURCE/TARGET" (e.g. "SETUP PEP FILES") — the name list alone missed those.
    if (task?.taskGroup?.toUpperCase() === 'CONFIG') return false;
    const excluded = ['CONFIGURE SOURCE', 'CONFIGURE TARGET', 'CONVERSIONS'];
    return !excluded.includes(taskName?.toUpperCase());
  };

  const handleSettingChange = (settingName, value) => {
    setFormSettings(prev => ({
      ...prev,
      [settingName]: value
    }));

    // Clear validation error for this field
    if (validationErrors[settingName]) {
      setValidationErrors(prev => {
        const updated = { ...prev };
        delete updated[settingName];
        return updated;
      });
    }
  };

  const togglePasswordVisibility = (settingName) => {
    setShowPasswords(prev => ({
      ...prev,
      [settingName]: !prev[settingName]
    }));
  };

  const toggleGroup = (groupName) => {
    setExpandedGroups(prev => ({
      ...prev,
      [groupName]: !prev[groupName]
    }));
  };

  const validateSettings = () => {
    const errors = {};

    if (task && task.taskSettings) {
      // When a credential is selected all base settings come from the credential — only
      // must_be_overridden fields (shown in the form) need to be validated.
      // If settings array is absent (credential just created inline) there are no overrides to validate.
      if (selectedShareId) {
        // Inherited credential: only must-be-overridden AUTH fields are shown, so only validate those.
        task.taskSettings.forEach(setting => {
          if (setting.mustBeOverridden !== 'Y') return;
          if (setting.requiredYn === 'Y' && !formSettings[setting.settingName]) {
            errors[setting.settingName] = 'This field is required';
          }
        });
      } else {
        task.taskSettings.forEach(setting => {
          if (setting.requiredYn === 'Y' && !formSettings[setting.settingName]) {
            errors[setting.settingName] = 'This field is required';
          }
        });
      }

      // A target connector's Hotel Id must be the project's hotel code — it decides which property every
      // record is written into. The server refuses a mismatch too (db.targetHotelIdProblem); this says so
      // before the request.
      const isTargetConnectivity = task.taskTarget === 'CONNECTIVITY' && task.taskName !== 'CONFIGURE SOURCE';
      const hotelIdSetting = isTargetConnectivity
        && task.taskSettings.find(s => String(s.settingName || '').toUpperCase() === 'HOTELID');
      if (hotelIdSetting && !errors[hotelIdSetting.settingName]) {
        const problem = targetHotelIdProblem(task.projectHotel, formSettings[hotelIdSetting.settingName]);
        if (problem) errors[hotelIdSetting.settingName] = problem;
      }
    }

    setValidationErrors(errors);

    if (Object.keys(errors).length > 0) {
      // Find the first erroring setting to determine group and tab
      const firstErrorSetting = task.taskSettings.find(s => errors[s.settingName]);
      if (firstErrorSetting) {
        // Expand the group containing the error
        const group = firstErrorSetting.settingGroup || 'General';
        setExpandedGroups(prev => ({ ...prev, [group]: true }));

        // Switch to the correct tab for CONNECTIVITY tasks
        if (task.taskTarget === 'CONNECTIVITY') {
          setSettingsTab(firstErrorSetting.settingType === 'AUTH' ? 'AUTH' : 'CONFIG');
        }
      }
    }

    return Object.keys(errors).length === 0;
  };

  const handleSave = async () => {
    if (!validateSettings()) {
      showToast('Please fix validation errors before saving', 'error');
      return;
    }

    try {
      setSaving(true);

      // ONLY the two connectivity tasks own a connector. This used to read "not CONFIGURE SOURCE, so
      // TARGET", which meant Save on a Conversions task wrote to the TARGET connector — blanking its
      // settings and unbinding its inherited credential. The server refuses it now too; this stops the
      // request being made at all.
      // A CONNECTIVITY task configures the source (CONFIGURE SOURCE) or the target (any other name — a
      // template may call it "SETUP PEP FILES"). The server applies the same rule (db.connectorRoleFor).
      const role = task.taskTarget !== 'CONNECTIVITY' ? null
        : task.taskName === 'CONFIGURE SOURCE' ? 'SOURCE' : 'TARGET';
      if (!role) {
        showToast('There is nothing to save on this task', 'info');
        setSaving(false);
        return;
      }
      const apiKeyToSave = task.usesApiKey === 'Y' && apiKey ? apiKey : undefined;

      const inheritedCredentialShareId = selectedShareId || null;
      // Entity-credentials only: never persist raw AUTH from the task screen. Submit only the
      // overridable AUTH fields (e.g. Hotel Id) plus all CONFIG settings — the rest come from the
      // linked entity credential via GETCONNECTORSETTING's fall-through. Writing an empty raw AUTH
      // value here would block that fall-through, so it must not be sent at all.
      const updatedSettings = (task.taskSettings || [])
        .filter(s => (s.settingType === 'AUTH' && s.mustBeOverridden === 'Y') || s.settingType !== 'AUTH')
        .map(s => ({ settingName: s.settingName, settingValue: formSettings[s.settingName] || '' }));

      await syncService.saveTaskSettings(syncId, taskId, updatedSettings, role, apiKeyToSave, inheritedCredentialShareId);

      showToast('Task configuration saved successfully', 'success');

      // Reload data to get fresh state
      await loadTaskData();

    } catch (err) {
      console.error('Error saving task configuration:', err);
      showToast(err.response?.data?.message || 'Failed to save configuration', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    navigate(`/sync/${syncId}?tab=${fromTab}`);
  };

  // Conversion handlers
  const handleAddConversion = () => {
    setEditingConversion(null);
    setShowConversionModal(true);
  };

  const handleEditConversion = (conversion) => {
    setEditingConversion(conversion);
    setShowConversionModal(true);
  };

  const handleDeleteConversion = async (conversionUuid) => {
    if (!window.confirm('Are you sure you want to delete this conversion?')) {
      return;
    }

    try {
      await syncService.deleteConversion(syncId, taskId, conversionUuid);
      showToast('Conversion deleted successfully', 'success');
      await loadConversions();
    } catch (err) {
      console.error('Error deleting conversion:', err);
      showToast('Failed to delete conversion', 'error');
    }
  };

  const handleConversionSave = async (conversionData) => {
    try {
      if (editingConversion) {
        await syncService.updateConversion(syncId, taskId, editingConversion.conversionUuid, conversionData);
        showToast('Conversion updated successfully', 'success');
      } else {
        await syncService.createConversion(syncId, taskId, conversionData);
        showToast('Conversion created successfully', 'success');
      }

      // Don't close here — the modal decides whether to stay open ("Save and add another")
      // or close itself via onClose(). Just refresh the list behind it.
      await loadConversions();
    } catch (err) {
      console.error('Error saving conversion:', err);
      throw err;
    }
  };

  const handleCSVUpload = async (file) => {
    try {
      // The server's own message, not a fixed string. It knows which of the two kinds it replaced and
      // how many rows it left alone because they belong to the other task, and a toast that always
      // said "Conversions uploaded successfully" reported that as a clean success on the Default
      // Values screen. The server also refuses a file holding nothing for this task, and that refusal
      // has to reach the user rather than be papered over.
      const result = await syncService.uploadConversionsCSV(syncId, taskId, file);
      const noun = isDefaultsTask ? 'Default values' : 'Conversions';
      showToast(result?.message || `${noun} uploaded successfully`, 'success');
      setShowCSVUploadModal(false);
      await loadConversions();
    } catch (err) {
      console.error('Error uploading CSV:', err);
      showToast(err.response?.data?.message || 'Failed to upload CSV', 'error');
    }
  };

  const handleCSVExampleDownload = () => {
    const rows = [['recordType', 'sourceFieldName', 'sourceValue', 'targetValue']];
    for (const type of conversionDataTypes) {
      for (const field of (type.fields || [])) {
        rows.push([type.scopeType, field.field, 'A', 'B']);
      }
    }
    const csv = rows.map(r => r.map(v => `"${v}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `conversions-example-${taskId}.csv`;
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
    document.body.removeChild(a);
  };

  const handleCSVDownload = async () => {
    try {
      const blob = await syncService.downloadConversionsCSV(syncId, taskId);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `conversions-${taskId}.csv`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      showToast('Conversions downloaded successfully', 'success');
    } catch (err) {
      console.error('Error downloading CSV:', err);
      showToast('Failed to download conversions', 'error');
    }
  };

  // Action handlers
  const handleExecuteAction = (actionName) => {
    setShowActionConfirm(actionName);
  };

  const handleConfirmAction = async () => {
    if (!showActionConfirm) return;

    try {
      setExecutingAction(showActionConfirm);
      await syncService.executeTaskAction(syncId, taskId, showActionConfirm);
      showToast(`Action "${showActionConfirm}" executed successfully`, 'success');
      setShowActionConfirm(null);
      
      // Reload summary after action
      if (shouldShowSummary(task?.taskName)) {
        await loadTaskSummary();
      }
    } catch (err) {
      console.error('Error executing action:', err);
      showToast(err.response?.data?.message || 'Failed to execute action', 'error');
    } finally {
      setExecutingAction(null);
    }
  };

  const handleTestConnection = async () => {
    try {
      setTestingConnection(true);
      const result = await syncService.testConnection(syncId, taskId);
      
      if (result.success) {
        setCommunicationStatus('SUCCESS');
        showToast('Connection test successful', 'success');
      } else {
        setCommunicationStatus('ERROR');
        showToast(result.message || 'Connection test failed', 'error');
      }
    } catch (err) {
      console.error('Error testing connection:', err);
      setCommunicationStatus('ERROR');
      showToast(err.response?.data?.message || 'Connection test failed', 'error');
    } finally {
      setTestingConnection(false);
    }
  };

  const doSendInstruction = async () => {
    setSendingInstruction(true);
    try {
      await syncService.sendInstruction(syncId, taskId);
      showToast('Instruction sent to source', 'success');
    } catch (err) {
      console.error('Error sending instruction:', err);
      showToast(err.response?.data?.message || 'Failed to send instruction', 'error');
    } finally {
      setSendingInstruction(false);
    }
  };

  const handleSendInstruction = () => {
    // Single-file export (task "Multiple File From Sync" = N): warn that this replaces everything.
    if (task?.multipleFileFromSync === 'N') {
      setShowSingleFileConfirm(true);
      return;
    }
    doSendInstruction();
  };

  // Remember the values a process was last run with, per task, so it can be reprocessed in one click.
  const lastProcessValuesKey = `processLastValues:${taskId}`;
  useEffect(() => {
    try {
      const raw = localStorage.getItem(lastProcessValuesKey);
      setLastProcessValues(raw ? JSON.parse(raw) : null);
    } catch (e) {
      setLastProcessValues(null);
    }
  }, [lastProcessValuesKey]);

  const handleInvokeProcess = async (settingValues = {}) => {
    setInvokingProcess(true);
    try {
      const res = await syncService.invokeProcess(syncId, taskId, settingValues);
      if (res && res.success === false) {
        showToast(res.message || 'Failed to invoke process', 'error');
      } else {
        showToast('Process invoked', 'success');
        // Remember these values so the user can reprocess with them in one click.
        if (settingValues && Object.keys(settingValues).length > 0) {
          try { localStorage.setItem(lastProcessValuesKey, JSON.stringify(settingValues)); } catch (e) { /* ignore */ }
          setLastProcessValues(settingValues);
        }
        // Re-fetch so the process-run status (Processing…) shows; refresh again shortly for Done.
        loadTaskData();
        setTimeout(() => loadTaskData(), 4000);
      }
    } catch (err) {
      console.error('Error invoking process:', err);
      showToast(err.response?.data?.message || 'Failed to invoke process', 'error');
    } finally {
      setInvokingProcess(false);
    }
  };

  // Upload the package an offline site exported; Run Process imports the newest one (sync-pep-import-offline).
  const isOfflineImportTask = task?.defaultProcess?.lambdaName === 'sync-pep-import-offline';
  // Which SyncConnect build packages are accepted from (V633) — the same summary the project's SyncConnect panel shows.
  const [offlineBuild, setOfflineBuild] = useState(null);
  useEffect(() => {
    if (!isOfflineImportTask) return undefined;
    let cancelled = false;
    syncService.getSyncConnectDownload(syncId)
      .then(d => { if (!cancelled) setOfflineBuild(d.offlineBuild || null); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [isOfflineImportTask, syncId, offlineCheck?.status]);
  const handleOfflinePackage = async (file) => {
    if (!file) return;
    if (!/\.hcx$/i.test(file.name)) {
      showToast('Choose the .hcx package the site exported', 'error');
      return;
    }
    setOfflineUploadPct(0);
    setOfflineCheck(null);
    let key = null;
    try {
      key = await syncService.uploadOfflinePackage(syncId, taskId, file, setOfflineUploadPct);
    } catch (err) {
      console.error('Error uploading offline package:', err);
      showToast(err.response?.data?.message || `Could not upload ${file.name}`, 'error');
      setOfflineUploadPct(null);
      if (offlinePackageInput.current) offlinePackageInput.current.value = '';
      return;
    }
    // CHECKED STRAIGHT AWAY: a package from an out-of-date SyncConnect (not the project's latest build) is refused
    // and deleted here, before anyone can import it.
    setOfflineUploadPct(null);
    setOfflineCheck({ status: 'CHECKING', fileName: file.name, message: `Checking ${file.name}…` });
    try {
      const verdict = await syncService.validateOfflinePackage(syncId, taskId, key);
      setOfflineCheck({ ...verdict, fileName: file.name });
    } catch (err) {
      console.error('Error checking offline package:', err);
      setOfflineCheck({ status: 'ERROR', fileName: file.name,
        message: err.response?.data?.message || 'The package could not be checked — Run Process checks it again before importing.' });
    } finally {
      if (offlinePackageInput.current) offlinePackageInput.current.value = '';
    }
  };

  // One-click reprocess: invoke immediately with the values the process was last run with (no modal).
  const handleReprocessSameValues = () => {
    if (!lastProcessValues) return;
    handleInvokeProcess(lastProcessValues);
  };

  // Run Process click: if the process's step has settings (e.g. START_DATE/END_DATE), prompt for them
  // first; otherwise fall through to the warning/plain confirm.
  const handleRunProcessClick = () => {
    const settings = task.defaultProcess?.settings || [];
    if (settings.length > 0) {
      const seed = {};
      // Pre-fill with the values last run (if any) so a re-run needs no retyping; else the step defaults.
      for (const s of settings) {
        seed[s.settingName] = (lastProcessValues && lastProcessValues[s.settingName] != null)
          ? lastProcessValues[s.settingName]
          : (s.settingValue || '');
      }
      setProcessSettingValues(seed);
      setProcessSettingsError(null);
      setShowProcessSettings(true);
    } else {
      setShowProcessConfirm(true);
    }
  };

  const handleRunWithSettings = () => {
    const settings = task.defaultProcess?.settings || [];
    for (const s of settings) {
      if (s.required === 'Y' && !String(processSettingValues[s.settingName] ?? '').trim()) {
        setProcessSettingsError(`${s.settingLabel || s.settingName} is required`);
        return;
      }
    }
    setProcessSettingsError(null);
    setShowProcessSettings(false);
    handleInvokeProcess(processSettingValues);
  };

  // Reconciliation handlers
  // opts.silent — used by the auto-refresh poll: skip the loading spinner and the page reset (and, on
  // error, keep the current records) so a background refresh doesn't flash the UI or bounce the user
  // back to page 1 / an empty table.
  const fetchReconRecords = async (customFilters = null, opts = {}) => {
    const { silent = false, keepPage = false } = opts;
    if (silent && reconLoadingIdRef.current) return;   // a search is running: its answer is the fresher one
    const requestId = ++reconRequestIdRef.current;
    try {
      if (!silent) {
        reconLoadingIdRef.current = requestId;
        setReconLoading(true);
      }
      setReconError(null);
      const filters = customFilters || {
        scopeType: reconScopeType || undefined,
        recordStatus: reconRecordStatus || undefined,
        search: reconSearch || undefined,
        showOnlyNullTargetId: reconShowOnlyNullTargetId || undefined,
      };
      const result = await syncService.searchReconciliationRecords(syncId, taskId, filters);
      if (requestId !== reconRequestIdRef.current) return;
      const records = Array.isArray(result) ? result : (result?.records || []);
      setReconRecords(records);
      setReconNextContinuationToken(null);
      setReconHasSearched(true);
      if (!silent && !keepPage) setReconPage(1);
    } catch (err) {
      if (requestId !== reconRequestIdRef.current) return;
      setReconError(err.response?.data?.message || err.message || 'Failed to load reconciliation records');
      if (!silent) {
        setReconRecords([]);
        setReconNextContinuationToken(null);
      }
    } finally {
      if (!silent && reconLoadingIdRef.current === requestId) {
        reconLoadingIdRef.current = 0;
        setReconLoading(false);
      }
    }
  };

  // Auto-refresh: poll the reconciliation records every 5s while the toggle is on. The ref always
  // points at the latest fetch closure so the poll uses the current filters/state, and it only fires
  // once a search has run (so it never queries before the user has loaded the list).
  const reconRefreshRef = useRef(() => {});
  reconRefreshRef.current = () => { if (reconHasSearched) fetchReconRecords(null, { silent: true }); };

  // Run the reconciliation search on arrival, so the page opens with the records already listed rather
  // than an empty table and a Search button. Any criteria held in sessionStorage are applied, so coming
  // back from a record's message log reproduces the list you left — and a first visit, with nothing
  // stored, searches unfiltered.
  //
  // The records are never themselves persisted: they would be stale on a live migration (status is the
  // whole reason you are looking), and a result set after a few "Load more" rounds does not belong in a
  // ~5MB store that throws when full. One query on mount is the cheaper and more correct half.
  //
  // keepPage preserves the page you were on — fetchReconRecords otherwise resets to page 1.
  // THE GUARD IS PER TASK, NOT PER MOUNT — and that was the bug.
  //
  // Switching task with the dropdown changes the route parameter without unmounting this page, so the
  // ref survived. It held `true` from the first task opened, this effect returned early for every task
  // after it, and no new search ran: the previous task's records stayed on screen under the new task's
  // heading. TR - TRACCOUNTINGCODES showed a PEP-POSCONFIGURATIONS record that belongs to another task
  // and does not exist in its scope — the summary above it correctly read 0, which is the pair of
  // numbers that gives it away.
  //
  // Holding the task the restore ran FOR, rather than a boolean, keeps it once-only per task (which is
  // what the guard is for — fetchReconRecords closes over the filter state, so listing that state as a
  // dependency would re-run the search on every keystroke) while still running once per task.
  //
  // THE LIST IS EMPTIED FIRST. The fetch is asynchronous, so without this the old task's rows are on
  // screen for as long as the request takes — and if it fails they stay indefinitely. A record shown
  // under the wrong task is worse than an empty table: it is a record somebody may act on.
  const reconRestoredRef = useRef(null);
  useEffect(() => {
    if (!syncId || !taskId) return;
    const key = `${syncId}:${taskId}`;
    if (reconRestoredRef.current === key) return;
    reconRestoredRef.current = key;
    setReconRecords([]);
    setReconSelectedRecords(new Set());
    setReconNextContinuationToken(null);
    setReconHasSearched(false);
    setReconError(null);
    setReconPage(1);
    fetchReconRecords(null, { keepPage: true });
  }, [syncId, taskId]);

  const summaryRefreshRef = useRef(() => {});
  summaryRefreshRef.current = () => loadTaskSummary({ silent: true });

  // The single 3s poll driving both halves of the merged card. Refs so the interval always calls the
  // current closure (live filters), and both are silent — a background poll must never throw a spinner
  // over a list you are reading, or reset the page you are on.
  //
  // ONLY ON A SCREEN THAT SHOWS WHAT IS BEING POLLED. The merged Summary + Reconciliation card is not on
  // a CONFIG task — ACCOUNTING-CODE-MAP, CONVERSIONS, CONFIGURE SOURCE/TARGET — so on those the timer was
  // fetching a summary and running a reconciliation search every three seconds for two panels that are
  // not rendered. Nothing appeared on screen, which is why it went unnoticed; what it cost was a pair of
  // requests per user per three seconds, one of them into OpenSearch, for as long as the tab was open.
  const [autoRefresh, setAutoRefresh] = useAutoRefresh('sync:autoRefresh', () => {
    summaryRefreshRef.current();
    reconRefreshRef.current();
  }, 3000, { enabled: !!task && shouldShowSummary(task.taskName) });

  // Manual refresh: same pair, same order, so the button and the toggle can never disagree.
  const refreshSummaryAndRecords = () => {
    loadTaskSummary();
    if (reconHasSearched) fetchReconRecords(null, { silent: true });
  };

  // Refresh only the Process Run status (silent — no full-page spinner, no form reset). Used by the
  // manual refresh button and the 5s auto-refresh toggle on the Process Run card.
  const refreshProcessRun = async ({ manual = false } = {}) => {
    if (manual) setProcessRefreshing(true);
    try {
      const taskData = await syncService.getTaskDetails(syncId, taskId);
      setTask(prev => (prev ? { ...prev, processRun: taskData.processRun } : taskData));
    } catch (e) {
      // Never surface a background poll error into the page.
      console.error('[refreshProcessRun]', e.message);
    } finally {
      if (manual) setProcessRefreshing(false);
    }
  };
  // Same rule for the Process Run poll: it exists to watch a run progress, so it runs where a run can be
  // seen — a process/schedule task, or any task that actually has a run on it.
  const [processAutoRefresh, setProcessAutoRefresh] = useAutoRefresh(
    'sync:processAutoRefresh', () => refreshProcessRun(), 5000,
    { enabled: !!task && (isProcessOrScheduleTask() || !!task.processRun) });

  const handleReconScopeClick = (scopeType) => {
    setReconScopeType(scopeType);
    setReconRecordStatus('');
    setReconSearch('');
    setReconShowOnlyNullTargetId(false);
    setReconSelectedRecords(new Set());
    setReconPage(1);
    fetchReconRecords({ scopeType: scopeType || undefined });
    // No scrollIntoView any more: the summary row you just clicked and the records it filters are in
    // the same card, so the old jump to the (separate) reconciliation card would now scroll UP, away
    // from the thing that was clicked.
  };

  const handleReconReset = () => {
    setReconScopeType('');
    setReconRecordStatus('');
    setReconSearch('');
    setReconShowOnlyNullTargetId(false);
    setReconRecords([]);
    setReconHasSearched(false);
    setReconError(null);
    setReconSelectedRecords(new Set());
    setReconNextContinuationToken(null);
    setReconPage(1);
  };

  const handleReconLoadMore = async () => {
    if (!reconNextContinuationToken) return;
    try {
      setReconLoadingMore(true);
      const filters = {
        scopeType: reconScopeType || undefined,
        recordStatus: reconRecordStatus || undefined,
        search: reconSearch || undefined,
        showOnlyNullTargetId: reconShowOnlyNullTargetId || undefined,
        continuationToken: reconNextContinuationToken,
      };
      const result = await syncService.searchReconciliationRecords(syncId, taskId, filters);
      const more = Array.isArray(result) ? result : (result?.records || []);
      setReconRecords(prev => [...prev, ...more]);
      setReconNextContinuationToken(result?.nextContinuationToken || null);
    } catch (err) {
      console.error('Error loading more records:', err);
    } finally {
      setReconLoadingMore(false);
    }
  };

  const handleReconSort = (column) => {
    if (reconSortBy === column) {
      setReconSortOrder(reconSortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setReconSortBy(column);
      setReconSortOrder('asc');
    }
  };

  const toggleReconSelectRecord = (recordId) => {
    setReconSelectedRecords(prev => {
      const next = new Set(prev);
      next.has(recordId) ? next.delete(recordId) : next.add(recordId);
      return next;
    });
  };

  // Superadmin: reprocess EVERY record in the chosen status — all of them, not the 1000 one search answers with. It
  // runs on the server (POST …/reconciliation/reprocess-all starts the sync-recon-bulk Step Function, which works
  // through the search a page at a time), so it carries on if this page is closed. The task's job is read on arrival
  // and polled while it runs, and its progress is shown under the filters.
  const [reconBulkJob, setReconBulkJob] = useState(null);
  const [reconReprocessAllConfirm, setReconReprocessAllConfirm] = useState(false);
  const [reconReprocessAllStarting, setReconReprocessAllStarting] = useState(false);
  const reconBulkRunning = reconBulkJob?.status === 'RUNNING';

  const loadReconBulkJob = async () => {
    try {
      const res = await syncService.getReprocessAllJob(syncId, taskId);
      setReconBulkJob(res?.job || null);
      return res?.job || null;
    } catch (e) {
      console.error('[reprocess-all status]', e.message);   // a background poll never surfaces into the page
      return null;
    }
  };

  useEffect(() => {
    setReconBulkJob(null);
    setReconReprocessAllConfirm(false);
    if (syncId && taskId && user?.isSuperAdmin) loadReconBulkJob();
  }, [syncId, taskId]); // eslint-disable-line

  useEffect(() => {
    if (!reconBulkRunning) return undefined;
    const timer = setInterval(async () => {
      const job = await loadReconBulkJob();
      // Finished: list the records again so the table shows what the job left behind.
      if (job && job.status !== 'RUNNING' && reconHasSearched) fetchReconRecords(null, { silent: true, keepPage: true });
    }, 3000);
    return () => clearInterval(timer);
  }, [reconBulkRunning, syncId, taskId]); // eslint-disable-line

  const handleReconReprocessAllInStatus = async () => {
    if (!reconRecordStatus) return;
    setReconReprocessAllConfirm(false);
    setReconReprocessAllStarting(true);
    try {
      const res = await syncService.startReprocessAll(syncId, taskId, {
        recordStatus: reconRecordStatus,
        search: reconSearch || undefined,
        showOnlyNullTargetId: reconShowOnlyNullTargetId || undefined,
      });
      setReconBulkJob(res?.job || null);
    } catch (err) {
      // 409: the task already has a job running — show that one instead.
      if (err.response?.data?.job) setReconBulkJob(err.response.data.job);
      showToast(err.response?.data?.message || 'Failed to start reprocessing', 'error');
    } finally {
      setReconReprocessAllStarting(false);
    }
  };

  // One line describing the job: what it is doing, or what it did.
  const reconBulkJobText = (job) => {
    const status = job.filters?.recordStatus || '';
    const verb = job.action === 'skip' ? 'skipped' : 'reprocessed';
    const n = (v) => Number(v || 0).toLocaleString();
    const extras = [
      Number(job.skipped_no_source) ? `${n(job.skipped_no_source)} without a source id` : null,
      Number(job.skipped_accepted) ? `${n(job.skipped_accepted)} stays OHIP had already accepted` : null,
      Number(job.profiles_requested) ? `${n(job.profiles_requested)} linked records requested` : null,
    ].filter(Boolean).join(', ');
    const tail = extras ? ` (${extras})` : '';
    if (job.status === 'RUNNING') {
      return `Working through every ${status} record on the server: ${n(job.matched)} found so far, ${n(job.updated)} ${verb}${tail}. It carries on if you leave this page.`;
    }
    if (job.status === 'DONE') {
      return `All ${status} records done ${formatDateTime(job.finished_at)}: ${n(job.updated)} of ${n(job.matched)} ${verb}${tail}.`;
    }
    return `Stopped ${formatDateTime(job.finished_at)} after ${n(job.updated)} of ${n(job.matched)} ${verb}${tail}: ${job.error || 'failed'}`;
  };

  const handleReconBulkAction = (action) => {
    if (reconSelectedRecords.size === 0) return;
    if (action === 'reprocess') {
      // Any selected record whose error names missing source profiles → offer to request them first.
      const withMissing = reconSelectedArray
        .map(record => ({ record, missingProfiles: parseMissingProfiles(record.comment) }))
        .filter(x => x.missingProfiles.length > 0);
      if (withMissing.length > 0) {
        setReconPendingAction('reprocess');
        setReconProfileWizardItems(withMissing);
        return;
      }
    }
    setReconPendingAction(action);
    setReconShowConfirmModal(true);
  };

  const handleReconConfirmAction = async (profileIds = []) => {
    if (!reconPendingAction || reconSelectedRecords.size === 0) return;
    try {
      setReconProcessingBulkAction(true);
      setReconShowConfirmModal(false);
      setReconProfileWizardItems(null);
      const recordIds = Array.from(reconSelectedRecords);
      const res = await syncService.bulkActionRecords(syncId, taskId, reconPendingAction, recordIds, profileIds);
      setReconBulkActionResult({
        count: res?.updated ?? recordIds.length,
        skippedNoSource: res?.skippedNoSource || 0,
        skippedAccepted: res?.skippedAccepted || 0,
        profilesRequested: res?.profilesRequested || 0,
        actionType: reconPendingAction,
      });
      setReconShowSuccessModal(true);
      setReconSelectedRecords(new Set());
      setReconPendingAction(null);
      await fetchReconRecords();
    } catch (err) {
      showToast(err.response?.data?.message || 'Failed to perform bulk action', 'error');
    } finally {
      setReconProcessingBulkAction(false);
    }
  };

  // Superadmin: request a single source record from the source system by source id + chosen scope.
  const handleRequestSource = async () => {
    const src = reqSourceId.trim();
    const scope = reqScope || reconScopeType; // fall back to the task's scope when the picker is untouched
    if (!src || !scope) return;
    setReqSubmitting(true);
    try {
      const res = await syncService.requestSource(syncId, taskId, src, scope);
      showToast(`Requested ${res.scopeType} ${res.sourceRecordId} (${res.messageType})`, 'success');
      setReqSourceId('');
      // Close on success only — a failure leaves the modal up with the id still in it, so the toast
      // can be read against what was typed and corrected, rather than the input vanishing with it.
      setShowRequestModal(false);
    } catch (e) {
      showToast(e.response?.data?.message || e.message || 'Request failed', 'error');
    } finally {
      setReqSubmitting(false);
    }
  };

  const handleReconDeleteClick = (deletable) => {
    if (deletable.length === 0) return;
    setReconRecordsToDelete(deletable);
    setReconShowDeleteModal(true);
  };

  const handleReconDeleteConfirm = async () => {
    try {
      setReconDeleting(true);
      const recordUuids = reconRecordsToDelete.map(r => r.recordUuid || r.recordid);
      await syncService.deleteReconciliationRecords(syncId, taskId, recordUuids);
      showToast(`${reconRecordsToDelete.length} record(s) deleted successfully`, 'success');
      setReconShowDeleteModal(false);
      setReconRecordsToDelete([]);
      setReconSelectedRecords(new Set());
      await fetchReconRecords();
    } catch (err) {
      showToast(err.response?.data?.message || 'Failed to delete records', 'error');
    } finally {
      setReconDeleting(false);
    }
  };

  // Open the CloudWatch Logs search in a new tab, pre-filtered to a source/target record id.
  const openCwLogsForRecord = (e, recordId) => {
    e.stopPropagation();
    if (!recordId) return;
    window.open(`/admin/opensearch/cwlogs?q=${encodeURIComponent(recordId)}`, '_blank', 'noopener');
  };

  // A record worth offering the request for. ERROR and DLQ reached the target and were refused; WARNING
  // means it synced with part of it rejected, which is also a call worth reading.
  const isFailedRecord = (record) =>
    ['ERROR', 'DLQ', 'WARNING'].includes(String(record?.recordStatus || '').toUpperCase());

  const openFailedExchange = async (record) => {
    if (!record.sprRecordUuid) return;
    setExchangeView({ record, loading: true, error: null, exchange: null });
    try {
      const res = await syncService.getFailedExchange(syncId, taskId, record.sprRecordUuid);
      setExchangeView({ record, loading: false, error: null, exchange: res?.exchange || null });
    } catch (err) {
      setExchangeView({
        record, loading: false, exchange: null,
        error: err.response?.data?.error?.message || err.response?.data?.message
          || 'Could not load the request for this record.',
      });
    }
  };

  // THE FILE A RECORD STORED (PEP extracts). Its targetRecordId is the S3 key, under this project's own
  // partition: step-0-extracted/<partner>/<chain>/<hotel>/<syncId>/… (records from before 2026-09-30 name the
  // same path without the step-0 folder; the server finds the file wherever it now is). The link carries nothing — clicking it asks the server
  // for a presigned URL valid five minutes, and the browser follows it straight away (the response names
  // the file as an attachment, so it downloads). No URL is ever stored or shown.
  const hasStoredFile = (record) => !!record?.sprRecordUuid
    && new RegExp(`^(step-0-extracted/)?\\d+/\\d+/\\d+/${syncId}/.+`).test(String(record.targetRecordId || ''));

  const [fileLinkBusy, setFileLinkBusy] = useState(null);
  // variant 'excel' opens the record's Excel copy; the busy marker names which link is working.
  const openRecordFile = async (e, record, variant = null) => {
    e.preventDefault();
    e.stopPropagation();
    setFileLinkBusy(`${record.sprRecordUuid}${variant ? `:${variant}` : ''}`);
    try {
      const res = await syncService.getRecordFileLink(syncId, taskId, record.sprRecordUuid, variant);
      if (res?.url) window.location.assign(res.url);
    } catch (err) {
      showToast(err.response?.data?.error?.message || err.response?.data?.message
        || 'Could not create a link to this file', 'error');
    } finally {
      setFileLinkBusy(null);
    }
  };

  // Open the record's message log — a full page (STYLE.md: detail views are pages, not modals).
  // The current filters travel in location.state so the page's Prev/Next walks the same list.
  const handleReconRowClick = (record) => {
    if (!record.sprRecordUuid) return;
    navigate(`/sync/${syncId}/task/${taskId}/reconciliation/${record.sprRecordUuid}/log`, {
      state: {
        backTo: `${window.location.pathname}${window.location.search}`,
        filters: {
          scopeType: reconScopeType || undefined,
          recordStatus: reconRecordStatus || undefined,
          search: reconSearch || undefined,
          showOnlyNullTargetId: reconShowOnlyNullTargetId || undefined,
        },
      },
    });
  };

  const handleReconDownloadCSV = async () => {
    try {
      setReconDownloading(true);
      const filters = {
        scopeType: reconScopeType || undefined,
        recordStatus: reconRecordStatus || undefined,
        search: reconSearch || undefined,
        showOnlyNullTargetId: reconShowOnlyNullTargetId || undefined,
      };
      const allRecords = await syncService.searchAllReconciliationRecords(syncId, taskId, filters, { download: true });
      const headers = ['Source Record ID', 'Target Record ID', 'Scope Type', 'Status', 'Stage', 'Error Message', 'Last Sync Time'];
      const rows = allRecords.map(r => [
        r.sourcerecordid ?? '',
        r.targetRecordId ?? '',
        r.scopeType ?? '',
        r.recordStatus ?? '',
        r.stage ?? '',
        r.comment ?? '',
        r.lastSyncTime ?? '',
      ].map(v => JSON.stringify(String(v))).join(','));
      const csv = [headers.join(','), ...rows].join('\n');
      const blob = new Blob([csv], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `reconciliation-${taskId}.csv`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      showToast(`Downloaded ${allRecords.length.toLocaleString()} records`, 'success');
    } catch (err) {
      showToast('Failed to download', 'error');
    } finally {
      setReconDownloading(false);
    }
  };

  const handleReconCSVUploadSuccess = async () => {
    showToast('Records uploaded. Refreshing...', 'success');
    setReconScopeType('');
    setReconRecordStatus('');
    setReconSearch('');
    setReconSelectedRecords(new Set());
    await fetchReconRecords({ scopeType: undefined, recordStatus: undefined });
  };

  const reconFilteredRecords = useMemo(() => {
    if (!reconShowOnlyNullTargetId) return reconRecords;
    return reconRecords.filter(r => !r.targetRecordId || r.targetRecordId.trim() === '');
  }, [reconRecords, reconShowOnlyNullTargetId]);

  const reconSortedRecords = useMemo(() => {
    const sorted = [...reconFilteredRecords];
    sorted.sort((a, b) => {
      let aVal = a[reconSortBy];
      let bVal = b[reconSortBy];
      if (!aVal && !bVal) return 0;
      if (!aVal) return 1;
      if (!bVal) return -1;
      if (reconSortBy === 'lastSyncTime') { aVal = new Date(aVal).getTime(); bVal = new Date(bVal).getTime(); }
      if (aVal < bVal) return reconSortOrder === 'asc' ? -1 : 1;
      if (aVal > bVal) return reconSortOrder === 'asc' ? 1 : -1;
      return 0;
    });
    return sorted;
  }, [reconFilteredRecords, reconSortBy, reconSortOrder]);

  const reconTotalRecords = reconSortedRecords.length;
  const reconTotalPages = Math.ceil(reconTotalRecords / reconPageSize);
  const reconPaginatedRecords = useMemo(() => {
    const start = (reconPage - 1) * reconPageSize;
    return reconSortedRecords.slice(start, start + reconPageSize);
  }, [reconSortedRecords, reconPage, reconPageSize]);

  const reconSelectableOnPage = reconPaginatedRecords.filter(r => r.recordid?.trim());
  const reconAllPageSelected = reconSelectableOnPage.length > 0 && reconSelectableOnPage.every(r => reconSelectedRecords.has(r.recordid.trim()));
  const reconSelectedArray = useMemo(() => reconRecords.filter(r => r.recordid?.trim() && reconSelectedRecords.has(r.recordid.trim())), [reconRecords, reconSelectedRecords]);
  // Any selected reconciliation record can be deleted — delete only removes the SPR tracking entry
  // (it does not touch the synced target data), so mapped, errored, duplicate or manual records can
  // all be cleaned up.
  const reconDeletable = reconSelectedArray;

  const toggleReconSelectAll = () => {
    if (reconAllPageSelected) {
      setReconSelectedRecords(prev => { const n = new Set(prev); reconSelectableOnPage.forEach(r => n.delete(r.recordid.trim())); return n; });
    } else {
      setReconSelectedRecords(prev => { const n = new Set(prev); reconSelectableOnPage.forEach(r => n.add(r.recordid.trim())); return n; });
    }
  };

  // Shared with TaskReconciliationPage and MessageLogPage — see utils/recordStatus.js.
  const getReconStatusColor = getRecordStatusColor;

  const renderReconSortIcon = (column) => {
    if (reconSortBy !== column) return null;
    return reconSortOrder === 'asc' ? <ChevronUp className="w-4 h-4 inline ml-1" /> : <ChevronDown className="w-4 h-4 inline ml-1" />;
  };

  const showToast = (message, type = 'info') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 5000);
  };

  // Parse pipe-separated KEY:Label options from availableValues
  const parseSelectOptions = (setting) => {
    if (!setting.availableValues) return [];
    return setting.availableValues.split('|').map(pair => {
      const [key, label] = pair.split(':');
      return { key, label: label || key };
    });
  };

  const getUniqueRecordTypes = useMemo(() => {
    const map = new Map();
    conversionDataTypes.forEach(dt => { if (dt.scopeType) map.set(dt.scopeType, dt.scopeDescription || dt.scopeType); });
    conversions.forEach(conv => { if (conv.recordType && !map.has(conv.recordType)) map.set(conv.recordType, conv.recordType); });
    return Array.from(map.entries()).sort((a, b) => a[1].localeCompare(b[1]));
  }, [conversions, conversionDataTypes]);

  const getFieldsForRecordType = useMemo(() => {
    const recordType = filterRecordType;
    if (!recordType) return [];
    const map = new Map();
    const dtEntry = conversionDataTypes.find(dt => dt.scopeType === recordType);
    if (dtEntry) dtEntry.fields.forEach(f => map.set(f.field, f.description || f.field));
    conversions.forEach(conv => {
      if (conv.recordType === recordType && conv.sourceFieldName && !map.has(conv.sourceFieldName)) {
        map.set(conv.sourceFieldName, conv.sourceFieldName);
      }
    });
    return Array.from(map.entries()).sort((a, b) => a[1].localeCompare(b[1]));
  }, [conversions, filterRecordType, conversionDataTypes]);

  const filteredConversions = useMemo(() => {
    const hasSource = (c) => (c.sourceValue ?? '').toString().trim() !== '';
    return conversions.filter(conv => {
      // Defaults task shows only empty-source rows; Conversions task shows only rows that have a source.
      if (isDefaultsTask ? hasSource(conv) : !hasSource(conv)) return false;
      if (filterRecordType && conv.recordType !== filterRecordType) return false;
      if (filterField && conv.sourceFieldName !== filterField) return false;
      if (filterSearch) {
        const search = filterSearch.toLowerCase();
        return (
          conv.sourceValue?.toLowerCase().includes(search) ||
          conv.targetValue?.toLowerCase().includes(search) ||
          conv.recordType?.toLowerCase().includes(search) ||
          conv.sourceFieldName?.toLowerCase().includes(search)
        );
      }
      return true;
    });
  }, [conversions, filterRecordType, filterField, filterSearch, isDefaultsTask]);

  // Group settings for display. On the CONFIG tab of a CONNECTIVITY task we group the CONFIG settings by
  // their SUB_GROUP (each sub group is a labelled section; settings with no sub group cluster under
  // "Other") so the tab reads better; non-CONFIG settings are excluded there. Everywhere else (AUTH tab,
  // non-connectivity tasks) group by the usual settingGroup.
  const groupBySubGroup = task?.taskTarget === 'CONNECTIVITY' && settingsTab === 'CONFIG';
  const groupedSettings = useMemo(() => {
    if (!task || !task.taskSettings) return {};

    const groups = {};
    task.taskSettings.forEach(setting => {
      let group;
      if (groupBySubGroup) {
        if (setting.settingType !== 'CONFIG') return; // CONFIG tab shows CONFIG settings only
        group = (setting.subGroup && String(setting.subGroup).trim()) || 'Other';
      } else {
        group = setting.settingGroup || 'General';
      }
      if (!groups[group]) {
        groups[group] = [];
      }
      groups[group].push(setting);
    });

    return groups;
  }, [task, groupBySubGroup]);

  const getStatusColor = (status) => {
    switch (status?.toUpperCase()) {
      case 'SUCCESS':
      case 'ONLINE':
        return 'status-success';
      case 'ERROR':
      case 'OFFLINE':
        return 'status-error';
      case 'WARNING':
        return 'status-warning';
      case 'PENDING':
        return 'status-pending';
      default:
        return 'status-default';
    }
  };

  if (loading) {
    return (
      <div className="task-config-loading">
        <Spinner size="lg" />
        <p>Loading task configuration...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="task-config-error">
        <Card>
          <div className="error-content">
            <AlertCircle className="error-icon" />
            <h2>Error Loading Task</h2>
            <p>{error}</p>
            <Button onClick={() => navigate(`/sync/${syncId}?tab=${fromTab}`)}>
              Back to Project
            </Button>
          </div>
        </Card>
      </div>
    );
  }

  if (!task) {
    return (
      <div className="task-config-error">
        <Card>
          <div className="error-content">
            <AlertCircle className="error-icon" />
            <h2>Task Not Found</h2>
            <Button onClick={() => navigate(`/sync/${syncId}?tab=${fromTab}`)}>
              Back to Project
            </Button>
          </div>
        </Card>
      </div>
    );
  }


  return (
    <div className="task-config-page">
      <div className="task-config-container">
        {/* Breadcrumb */}
        <div className="breadcrumb">
          <Link to="/sync" className="breadcrumb-link">
            Sync Projects
          </Link>
          <span className="breadcrumb-separator">/</span>
          <Link to={`/sync/${syncId}`} className="breadcrumb-link">
            {project?.projectDetails?.projectName || 'Project'}
            {project?.projectDetails?.hotelName && <span style={{ fontWeight: 400, color: '#6b7280' }}> ({project.projectDetails.hotelName})</span>}
          </Link>
          <span className="breadcrumb-separator">/</span>
          <span className="breadcrumb-current">{task.taskName}</span>
        </div>

        {/* Header */}
        <div className="task-config-header">
          <div>
            <div className="header-top">
              <h1>{task.taskName}</h1>
              {task.taskStatus && (
                <span className={`task-status-badge ${getStatusColor(task.taskStatus)}`}>
                  {task.taskStatus}
                </span>
              )}
            </div>
            <p className="task-description">{task.taskDescription || 'Configure task settings and conversions'}</p>
            {task.lastMessageAt && (
              <p className="task-description" style={{ fontSize: '0.8rem', color: '#6b7280', marginTop: '0.25rem' }}>
                Last message: {new Date(task.lastMessageAt).toLocaleString()}
              </p>
            )}
          </div>

          {/* Task switcher — sits between the task identity and its actions, so the header answers
              "which task am I on, and which others are there" in one place.
              This replaced a Previous / "Task 1 of 9" / Next strip: stepping one task at a time cost a
              page load per step and never showed what the neighbouring tasks were. */}
          {taskNav.total > 1 && taskNav.index !== -1 && (
            <div className="task-switcher">
              <label className="task-switcher-label" htmlFor="task-switcher">Task</label>
              {/* Option values are ids, and so is `value` — taken from the MATCHED task rather than
                  straight from the URL, so a legacy uuid link still selects the right option instead
                  of leaving the control blank. */}
              <select
                id="task-switcher"
                className="form-input task-switcher-select"
                value={taskNav.index === -1 ? '' : taskKey(taskNav.ordered[taskNav.index])}
                onChange={e => goToTask(taskNav.ordered.find(t => taskKey(t) === e.target.value))}
              >
                {taskNav.ordered.map((t, i) => (
                  <option key={taskKey(t)} value={taskKey(t)}>
                    {i + 1}. {t.taskName}
                  </option>
                ))}
              </select>
              {/* Position is still worth stating — the list is ordered, and "3 of 9" says how far
                  through the run this task sits without counting the options. */}
              <span className="task-switcher-count">{taskNav.index + 1} of {taskNav.total}</span>
            </div>
          )}

          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '8px' }}>
            <Button
              variant="success"
              onClick={handleSendInstruction}
              disabled={sendingInstruction || task.showInstructionButtonYn !== 'Y' || !task.defaultInstruction}
              style={{ background: '#16a34a', borderColor: '#16a34a', color: '#fff', fontWeight: 600 }}
            >
              {sendingInstruction ? (
                <>
                  <Spinner size="sm" className="icon-sm" />
                  Sending...
                </>
              ) : (
                <>
                  <FileText className="icon-sm" />
                  Send Instruction to Source
                </>
              )}
            </Button>
            {task.showInstructionButtonYn !== 'Y' ? (
              <span style={{ fontSize: '0.75rem', color: '#9ca3af' }}>Instruction not enabled for this task</span>
            ) : task.defaultInstruction ? (
              <div style={{ textAlign: 'right', maxWidth: '280px' }}>
                <div style={{ fontSize: '0.72rem', color: '#6b7280' }}>Will send instruction:</div>
                <div style={{ fontSize: '0.75rem', fontWeight: 600, color: '#374151' }}>{task.defaultInstruction.title}</div>
                {task.defaultInstruction.matchedBy && (
                  <div style={{ marginTop: '2px' }}>
                    <span style={{
                      fontSize: '0.7rem', fontWeight: 600, borderRadius: '20px',
                      padding: '0.1rem 0.45rem',
                      ...(task.defaultInstruction.matchedBy === 'hotel'   ? { background: '#eef2ff', color: '#4338ca' } :
                          task.defaultInstruction.matchedBy === 'chain'   ? { background: '#dcfce7', color: '#166534' } :
                          task.defaultInstruction.matchedBy === 'country' ? { background: '#fef3c7', color: '#92400e' } :
                                                                            { background: '#f3f4f6', color: '#6b7280' })
                    }}>
                      {task.defaultInstruction.matchedBy === 'hotel'   ? 'Hotel match' :
                       task.defaultInstruction.matchedBy === 'chain'   ? 'Chain match' :
                       task.defaultInstruction.matchedBy === 'country' ? 'Country match' :
                                                                         'Default'}
                    </span>
                  </div>
                )}
              </div>
            ) : (
              <span style={{ fontSize: '0.75rem', color: '#9ca3af' }}>No instructions set up to send</span>
            )}

            {task.showProcessButtonYn === 'Y' && (
              <>
                {isOfflineImportTask && (
                  <>
                    <input
                      ref={offlinePackageInput}
                      type="file"
                      accept=".hcx"
                      style={{ display: 'none' }}
                      onChange={(e) => handleOfflinePackage(e.target.files && e.target.files[0])}
                    />
                    <Button
                      variant="secondary"
                      onClick={() => offlinePackageInput.current && offlinePackageInput.current.click()}
                      disabled={offlineUploadPct !== null || offlineCheck?.status === 'CHECKING' || invokingProcess}
                      title="Upload the package (.hcx) the site exported from offline SyncConnect"
                    >
                      {offlineCheck?.status === 'CHECKING' ? (
                        <>
                          <Spinner size="sm" className="icon-sm" />
                          Checking package…
                        </>
                      ) : offlineUploadPct !== null ? (
                        <>
                          <Spinner size="sm" className="icon-sm" />
                          Uploading {offlineUploadPct}%
                        </>
                      ) : (
                        <>
                          <Upload className="icon-sm" />
                          Upload Package
                        </>
                      )}
                    </Button>
                  </>
                )}
                <Button
                  variant="success"
                  onClick={handleRunProcessClick}
                  disabled={invokingProcess || !task.defaultProcess}
                  style={{ background: '#16a34a', borderColor: '#16a34a', color: '#fff', fontWeight: 600 }}
                >
                  {invokingProcess ? (
                    <>
                      <Spinner size="sm" className="icon-sm" />
                      Running...
                    </>
                  ) : (
                    <>
                      <Play className="icon-sm" />
                      Run Process
                    </>
                  )}
                </Button>
                {task.defaultProcess && (task.defaultProcess.settings || []).length > 0 && lastProcessValues && (
                  <Button
                    variant="secondary"
                    onClick={handleReprocessSameValues}
                    disabled={invokingProcess}
                    title="Reprocess immediately with the parameters last used — no retyping"
                  >
                    <RefreshCw className="icon-sm" />
                    Reprocess (same values)
                  </Button>
                )}
                {task.defaultProcess ? (
                  <div style={{ textAlign: 'right', maxWidth: '320px' }}>
                    <div style={{ fontSize: '0.72rem', color: '#6b7280' }}>Will invoke:</div>
                    <div style={{ fontSize: '0.75rem', fontWeight: 600, color: '#374151' }}>{task.defaultProcess.name}</div>
                    {task.defaultProcess.lambdaName && (
                      <div style={{ fontSize: '0.7rem', color: '#6b7280', fontFamily: 'monospace' }}>{task.defaultProcess.lambdaName}</div>
                    )}
                    {(task.defaultProcess.settings || []).length > 0 && lastProcessValues && (() => {
                      const params = (task.defaultProcess.settings || [])
                        .filter((s) => String(lastProcessValues[s.settingName] ?? '').trim() !== '');
                      if (!params.length) return null;
                      return (
                        <div style={{ marginTop: 4, paddingTop: 4, borderTop: '1px dashed #e5e7eb' }}>
                          <div style={{ fontSize: '0.7rem', color: '#6b7280' }}>Reprocess parameters:</div>
                          {params.map((s) => (
                            <div key={s.settingName} style={{ fontSize: '0.72rem', color: '#374151' }}>
                              <span style={{ color: '#6b7280' }}>{s.settingLabel || s.settingName}:</span>{' '}
                              <span style={{ fontWeight: 600 }}>{String(lastProcessValues[s.settingName])}</span>
                            </div>
                          ))}
                        </div>
                      );
                    })()}
                    {task.defaultProcess.matchedBy && (
                      <div style={{ marginTop: '2px' }}>
                        <span style={{
                          fontSize: '0.7rem', fontWeight: 600, borderRadius: '20px',
                          padding: '0.1rem 0.45rem',
                          ...(task.defaultProcess.matchedBy === 'hotel'   ? { background: '#eef2ff', color: '#4338ca' } :
                              task.defaultProcess.matchedBy === 'chain'   ? { background: '#dcfce7', color: '#166534' } :
                              task.defaultProcess.matchedBy === 'country' ? { background: '#fef3c7', color: '#92400e' } :
                                                                            { background: '#f3f4f6', color: '#6b7280' })
                        }}>
                          {task.defaultProcess.matchedBy === 'hotel'   ? 'Hotel match' :
                           task.defaultProcess.matchedBy === 'chain'   ? 'Chain match' :
                           task.defaultProcess.matchedBy === 'country' ? 'Country match' :
                                                                         'Default'}
                        </span>
                      </div>
                    )}
                  </div>
                ) : (
                  <span style={{ fontSize: '0.75rem', color: '#9ca3af' }}>No processes set up</span>
                )}
              </>
            )}
          </div>
        </div>

        {/* The verdict on the offline package just uploaded (accepted / refused and why). A refused package has
            already been deleted on the hub, so what this says is what to do at the site. */}
        {isOfflineImportTask && offlineBuild && (
          <div style={{ margin: '0.5rem 0', fontSize: '0.8rem', color: '#4b5563' }}>
            {offlineBuild.latest
              ? <>Packages are accepted only from <strong>SyncConnect build {offlineBuild.latest.buildNumber}</strong>{' '}
                  (generated {new Date(offlineBuild.latest.succeededAt || offlineBuild.latest.createdAt).toLocaleDateString()}).</>
              : <>No SyncConnect build has succeeded for this project yet — every package will be refused until one has.</>}
            {offlineBuild.outOfDate && offlineBuild.latest && (
              <span style={{ color: '#b45309', fontWeight: 600 }}> The template has changed since then — generate SyncConnect again and reinstall it at the site.</span>
            )}
          </div>
        )}
        {isOfflineImportTask && offlineCheck && (() => {
          const tone = {
            CHECKING: { bg: '#eff6ff', border: '#bfdbfe', color: '#1e40af', label: 'Checking' },
            ACCEPTED: { bg: '#f0fdf4', border: '#86efac', color: '#166534', label: 'Accepted' },
            REFUSED:  { bg: '#fef2f2', border: '#fca5a5', color: '#991b1b', label: 'Refused' },
          }[offlineCheck.status] || { bg: '#fffbeb', border: '#fcd34d', color: '#92400e', label: 'Not checked' };
          return (
            <div role="status" style={{ margin: '0.75rem 0', padding: '0.75rem 1rem', borderRadius: 8,
              background: tone.bg, border: `1px solid ${tone.border}`, color: tone.color, display: 'flex', gap: '0.75rem', alignItems: 'flex-start' }}>
              <div style={{ flex: 1 }}>
                <strong>{tone.label}: {offlineCheck.fileName}</strong>
                <div style={{ marginTop: 2 }}>{offlineCheck.message}</div>
                {offlineCheck.status === 'REFUSED' && (
                  <div style={{ marginTop: 4, fontSize: '0.8rem' }}>The package has been removed — nothing from it can be imported.</div>
                )}
              </div>
              {offlineCheck.status !== 'CHECKING' && (
                <button type="button" onClick={() => setOfflineCheck(null)} aria-label="Dismiss"
                  style={{ background: 'none', border: 'none', color: tone.color, cursor: 'pointer', fontSize: '1rem' }}>×</button>
              )}
            </div>
          );
        })()}

        {/* Process Run status — written by the process lambda (Processing at start, Done at end). */}
        {task.showProcessButtonYn === 'Y' && task.processRun && (() => {
          const badge = (s) => {
            const u = (s || '').toUpperCase();
            if (u === 'PROCESSING') return { background: '#cffafe', color: '#155e75' };
            if (u === 'DONE') return { background: '#dcfce7', color: '#166534' };
            if (u === 'ERROR' || u === 'FAILED') return { background: '#fee2e2', color: '#991b1b' };
            if (u === 'STALLED') return { background: '#fef3c7', color: '#92400e' };
            return { background: '#f3f4f6', color: '#6b7280' };
          };
          const chip = (s) => (
            <span style={{ fontSize: '0.72rem', fontWeight: 700, borderRadius: '20px', padding: '0.12rem 0.5rem', ...badge(s) }}>{s || '—'}</span>
          );
          const history = Array.isArray(task.processRun.history) ? task.processRun.history : [];
          // Live progress written by the running step. markProcessRun only writes at start and end, so
          // without this a multi-hour run shows "Process started" the whole time and you cannot tell
          // working from hung — the heartbeat below is what distinguishes them.
          const prog = task.processRun.progress || null;
          const counters = prog && prog.counters ? Object.entries(prog.counters).filter(([, c]) => c && typeof c.value === 'number') : [];
          const pct = prog && prog.total > 0 ? Math.min(100, Math.round(((prog.done || 0) / prog.total) * 100)) : null;
          const beatAgeMs = prog && prog.heartbeatAt ? Date.now() - new Date(prog.heartbeatAt).getTime() : null;
          const stalled = (task.processRun.status || '').toUpperCase() === 'PROCESSING' && beatAgeMs != null && beatAgeMs > 10 * 60 * 1000;
          const ago = (ms) => (ms < 60000 ? `${Math.round(ms / 1000)}s ago` : ms < 3600000 ? `${Math.round(ms / 60000)}m ago` : `${Math.round(ms / 3600000)}h ago`);
          return (
            <Card>
              <div className="card-header" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <h2>Process Run</h2>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <button
                    type="button"
                    className="pp-toggle-btn pp-toggle-btn-sm pp-toggle-btn-off"
                    onClick={() => refreshProcessRun({ manual: true })}
                    disabled={processRefreshing}
                    title="Refresh the process run status now"
                  >
                    <RefreshCw className={`icon-sm ${processRefreshing ? 'spin' : ''}`} />
                    <span className="pp-toggle-label">Refresh</span>
                  </button>
                  <button
                    type="button"
                    className={`pp-toggle-btn pp-toggle-btn-sm ${processAutoRefresh ? 'pp-toggle-btn-on' : 'pp-toggle-btn-off'}`}
                    onClick={() => setProcessAutoRefresh(v => !v)}
                    title={processAutoRefresh ? 'Auto-refresh is on — status reloads every 5s' : 'Turn on to reload status every 5s'}
                  >
                    <span className="pp-toggle-track"><span className="pp-toggle-thumb" /></span>
                    <span className="pp-toggle-label">Auto-refresh 5s</span>
                  </button>
                </div>
              </div>
              <div style={{ padding: '12px 20px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                  {chip(task.processRun.status)}
                  {stalled && chip('STALLED')}
                  {task.processRun.updatedAt && <span style={{ fontSize: '0.8rem', color: '#6b7280' }}>{new Date(task.processRun.updatedAt).toLocaleString()}</span>}
                  {beatAgeMs != null && (
                    <span style={{ fontSize: '0.8rem', color: stalled ? '#92400e' : '#6b7280' }}>
                      updated {ago(beatAgeMs)}
                    </span>
                  )}
                </div>
                {prog && (prog.phase || pct != null) && (
                  <div style={{ marginTop: 10 }}>
                    <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', fontSize: '0.8rem', color: '#4b5563' }}>
                      <span>{prog.phase || ''}{prog.phaseDetail ? ` — ${prog.phaseDetail}` : ''}</span>
                      {pct != null && <span>{prog.done || 0}/{prog.total} ({pct}%)</span>}
                    </div>
                    {pct != null && (
                      <div style={{ marginTop: 4, height: 6, borderRadius: 999, background: '#f3f4f6', overflow: 'hidden' }}>
                        <div style={{ width: `${pct}%`, height: '100%', background: '#4a6cf7', transition: 'width 0.3s' }} />
                      </div>
                    )}
                  </div>
                )}
                {counters.length > 0 && (
                  <div style={{ marginTop: 10, display: 'flex', flexWrap: 'wrap', gap: '0.4rem' }}>
                    {counters.map(([key, c]) => {
                      const danger = /error|fail|incomplete/i.test(key) && c.value > 0;
                      return (
                        <span
                          key={key}
                          style={{
                            fontSize: '0.72rem', fontWeight: 600, borderRadius: 20, padding: '0.15rem 0.55rem',
                            background: danger ? '#fee2e2' : '#eef2ff', color: danger ? '#b91c1c' : '#4338ca',
                          }}
                        >
                          {c.label || key}: {c.value.toLocaleString()}
                        </span>
                      );
                    })}
                  </div>
                )}
                {/* THE RUN AS A TABLE when the process wrote one (PEP run, accounting codes): per data type
                    outcome and reason, with every file it produced downloadable. The paragraph comment is
                    the fallback for processes that write no report. */}
                {task.processRun.report && (task.processRun.status || '').toUpperCase() !== 'PROCESSING' ? (
                  <ProcessRunReport
                    report={task.processRun.report}
                    onDownload={async (key) => {
                      try {
                        const res = await syncService.getProcessFileLink(syncId, taskId, key);
                        if (res?.url) window.location.assign(res.url);
                      } catch (err) {
                        showToast(err.response?.data?.error?.message || err.response?.data?.message
                          || 'Could not create a link to this file', 'error');
                      }
                    }}
                  />
                ) : task.processRun.comment && (
                  <div style={{ marginTop: 8, fontSize: '0.85rem', color: '#374151' }}>{task.processRun.comment}</div>
                )}
                {history.length > 0 && (
                  <details style={{ marginTop: 10 }}>
                    <summary style={{ cursor: 'pointer', fontSize: '0.8rem', color: '#2563eb' }}>Run history ({history.length})</summary>
                    <table style={{ marginTop: 6, width: '100%', fontSize: '0.8rem', borderCollapse: 'collapse' }}>
                      <tbody>
                        {[...history].reverse().map((h, i) => (
                          <tr key={i}>
                            <td style={{ padding: '3px 8px 3px 0' }}>{chip(h.status)}</td>
                            <td style={{ padding: '3px 8px', color: '#4b5563' }} title={h.comment || ''}>
                              {h.comment && h.comment.length > 180 ? `${h.comment.slice(0, 180)}…` : (h.comment || '')}
                            </td>
                            <td style={{ color: '#9ca3af', whiteSpace: 'nowrap' }}>{h.at ? new Date(h.at).toLocaleString() : ''}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </details>
                )}
              </div>
            </Card>
          );
        })()}

        {/* Communication Status */}
        {communicationStatus && (
          <Card className="communication-status-card">
            <div className="communication-status">
              <div className="status-info">
                <h3>Communication Status</h3>
                <span className={`status-badge ${getStatusColor(communicationStatus)}`}>
                  {communicationStatus}
                </span>
              </div>
              <Button
                variant="secondary"
                onClick={handleTestConnection}
                disabled={testingConnection}
              >
                {testingConnection ? (
                  <>
                    <Spinner size="sm" className="icon-sm" />
                    Testing...
                  </>
                ) : (
                  <>
                    <RefreshCw className="icon-sm" />
                    Test Connection
                  </>
                )}
              </Button>
            </div>
          </Card>
        )}

        {/* Connector */}
        {task.connectorId && (
          <Card>
            <div className="card-header">
              <h2>Connector</h2>
            </div>
            <div style={{ display: 'flex', gap: '16px', alignItems: 'flex-start' }}>
              {task.usesApiKey === 'Y' && (
                <ApiKeyField
                  id="task-api-key"
                  value={apiKey}
                  onChange={setApiKey}
                  required
                  onCopied={() => showToast('API key copied to clipboard', 'success')}
                  className="api-key-field-flex"
                />
              )}
              <div className="form-group" style={{ flex: 1 }}>
                <label className="form-label">Connector ID</label>
                <input
                  type="text"
                  className="form-input"
                  value={task.connectorId}
                  readOnly
                  disabled
                />
              </div>
              {task.connectorSystemId && (
                <div className="form-group" style={{ flex: 1 }}>
                  <label className="form-label">{task.connectorSystemName || 'System'}</label>
                  <input
                    type="text"
                    className="form-input"
                    value={task.connectorSystemId}
                    readOnly
                    disabled
                  />
                </div>
              )}
            </div>

            {/* OUTSIDE the field row: the diagram carries full URLs, so it needs the whole card width
                rather than the API Key column's share of it. No wrapper padding — the card supplies its
                own, and the diagram should line up with the fields above it. */}
            {SYNC_API_BASE && isOxiConnector(task) && (
              <OxiTopologyDiagram
                syncHost={SYNC_HOST}
                apiKey={apiKey}
                usesSyncConnect={project?.projectDetails?.usesSyncConnect}
                onCopy={(text, msg) => { navigator.clipboard.writeText(text); showToast(msg, 'success'); }}
              />
            )}
          </Card>
        )}

        {/* Troubleshooting — a check the PROPERTY runs, not us. It proves two things at once from the
            OPERA/OXI machine itself: that outbound HTTPS to the hub is open, and that the API key is the
            right one. Kept as its own section so it can be read out over the phone to on-site staff. */}
        {task.connectorId && task.usesApiKey === 'Y' && SYNC_API_BASE && (
          <Card>
            <div className="card-header">
              <h2>Troubleshooting</h2>
            </div>
            <div>
              <label className="form-label">Check the property can reach hconnect</label>
              <ol className="tsg-steps">
                <li>
                  On the machine running OPERA / OXI <strong>at the property</strong>, open a Command Prompt —
                  press <kbd>Windows</kbd> + <kbd>R</kbd>, type <code>cmd</code> and press <kbd>Enter</kbd>.
                </li>
                <li>Paste this in and press <kbd>Enter</kbd>:</li>
              </ol>
              <ApiKeyUrlBlock
                label=""
                hint={
                  <>
                    A reply of {'{ "syncProjectId": … }'} confirms the property can reach hconnect and that this
                    API key is valid. A timeout, “Could not resolve host” or “Failed to connect” means outbound
                    HTTPS to <code>{SYNC_HOST.replace(/^https?:\/\//, '')}</code> on port 443 is blocked — ask the
                    property’s network team to allow it.
                  </>
                }
                apiKey={apiKey}
                prefix="curl "
                buildUrl={(key) => `${SYNC_HOST}/${key}/check`}
                onCopy={(text, msg) => { navigator.clipboard.writeText(text); showToast(msg, 'success'); }}
                copyMessage="Command copied to clipboard"
              />
              <p className="form-hint" style={{ marginTop: '10px' }}>
                <code>curl</code> ships with Windows 10 and later, so nothing needs installing.
              </p>
            </div>
          </Card>
        )}

        {/* Connection credentials — shared component used by sync, PP, vault */}
        {task.taskTarget === 'CONNECTIVITY' && task.connectorSystemId && (
          <Card>
            <div className="card-header">
              <h2>Connection Credentials</h2>
            </div>
            <div style={{ padding: '16px 20px' }}>
              <CredentialSelector
                marketplaceId={task.connectorSystemId}
                hotelId={project?.projectDetails?.hotelId}
                accountId={project?.projectDetails?.accountId}
                partnerId={project?.projectDetails?.partnerId}
                requiresShareId
                canEdit={user?.isSuperAdmin}
                title={null}
                subtitle="Choose an existing credential from the partner, chain or hotel — or add a new one at any level."
                value={selectedSharedCred}
                onChange={(sel) => (sel ? applySelectedCredential(sel) : handleRemoveCredential())}
              />
            </div>
          </Card>
        )}

        {/* Settings Form */}
        {task.taskSettings && task.taskSettings.length > 0 && (
          <Card>
            <div className="card-header">
              <h2>Task Settings</h2>
              <div className="header-actions">
                {/* Configure Source / Configure Target only: these are connector settings, and the
                    thing worth copying is another property already wired to the same system. Offered
                    on no other task because nothing else here is comparable across projects. */}
                {task.taskTarget === 'CONNECTIVITY' && !shouldShowSummary(task.taskName) && (
                  <Button variant="secondary" onClick={() => setShowCopySettingsModal(true)}>
                    <Copy className="icon-sm" />
                    Copy from project
                  </Button>
                )}
                {/* Save co-located with the fields on editable config tasks — same action + gate as the
                    sticky footer (now correct for any CONFIG-phase task, incl. "SETUP PEP FILES"). */}
                {!shouldShowSummary(task.taskName) && (
                  <Button onClick={handleSave} disabled={saving}>
                    {saving ? 'Saving…' : 'Save'}
                  </Button>
                )}
              </div>
            </div>

            {/* AUTH / CONFIG tabs for CONNECTIVITY tasks */}
            {task.taskTarget === 'CONNECTIVITY' && (
              <div className="settings-type-tabs">
                {['AUTH', 'CONFIG'].map(tab => (
                  <button
                    key={tab}
                    type="button"
                    className={`settings-type-tab ${settingsTab === tab ? 'active' : ''}`}
                    onClick={() => setSettingsTab(tab)}
                  >
                    {tab}
                  </button>
                ))}
              </div>
            )}

            <div className="settings-groups">
              {Object.entries(groupedSettings)
                .sort((a, b) => (a[0] === 'Other' ? 1 : 0) - (b[0] === 'Other' ? 1 : 0)) // ungrouped ("Other") last
                .map(([groupName, settings]) => {
                const visibleSettings = task.taskTarget === 'CONNECTIVITY'
                  ? settings.filter(s => {
                      if (settingsTab === 'AUTH') {
                        if (s.settingType !== 'AUTH') return false;
                        // Entity-credentials only: raw AUTH values always come from the linked
                        // credential (the Connection Credentials picker above), so never show them
                        // here. Only show AUTH fields the marketplace marks as overridable
                        // (e.g. Hotel Id), which vary per hotel/task even with a shared credential.
                        return s.mustBeOverridden === 'Y';
                      }
                      // CONFIG tab: show ONLY CONFIG-type settings (non-CONFIG types are hidden here).
                      // Inherited credentials: hide AUTH settings that come from the credential.
                      if (selectedShareId && s.settingType === 'AUTH') return false;
                      return s.settingType === 'CONFIG';
                    })
                  : settings;
                if (visibleSettings.length === 0) return null;
                return (
                <div key={groupName} className="settings-group">
                  <button
                    className="group-header"
                    onClick={() => toggleGroup(groupName)}
                  >
                    <h3>{groupName}</h3>
                    {expandedGroups[groupName] ? (
                      <ChevronUp className="icon-sm" />
                    ) : (
                      <ChevronDown className="icon-sm" />
                    )}
                  </button>

                  {expandedGroups[groupName] && (
                    <div className="group-content">
                      {visibleSettings.map(setting => (
                        <div key={setting.settingName} className="form-group">
                          <label className="form-label">
                            {setting.settingLabel || setting.settingName}
                            {setting.requiredYn === 'Y' && <span className="required">*</span>}
                          </label>

                          {setting.dataType === 'SELECT' && (
                            <select
                              className={`form-input ${validationErrors[setting.settingName] ? 'error' : ''}`}
                              value={formSettings[setting.settingName] || ''}
                              onChange={(e) => handleSettingChange(setting.settingName, e.target.value)}
                            >
                              <option value="">Select...</option>
                              {parseSelectOptions(setting).map(opt => (
                                <option key={opt.key} value={opt.key}>{opt.label}</option>
                              ))}
                            </select>
                          )}

                          {setting.dataType === 'MULTISELECT' && (
                            <div className="multiselect-group">
                              {parseSelectOptions(setting).map(opt => {
                                const selected = (formSettings[setting.settingName] || '').split(',');
                                return (
                                  <label key={opt.key} className="multiselect-option">
                                    <input
                                      type="checkbox"
                                      checked={selected.includes(opt.key)}
                                      onChange={() => {
                                        const next = selected.includes(opt.key)
                                          ? selected.filter(k => k !== opt.key)
                                          : [...selected.filter(Boolean), opt.key];
                                        handleSettingChange(setting.settingName, next.join(','));
                                      }}
                                    />
                                    {opt.label}
                                  </label>
                                );
                              })}
                            </div>
                          )}

                          {setting.dataType === 'TEXTAREA' && (
                            <textarea
                              className={`form-input ${validationErrors[setting.settingName] ? 'error' : ''}`}
                              value={formSettings[setting.settingName] || ''}
                              onChange={(e) => handleSettingChange(setting.settingName, e.target.value)}
                              placeholder={setting.placeholder || ''}
                              rows={4}
                            />
                          )}

                          {setting.dataType === 'PASSWORD' && (
                            <div className="password-input">
                              <input
                                type={showPasswords[setting.settingName] ? 'text' : 'password'}
                                className={`form-input ${validationErrors[setting.settingName] ? 'error' : ''}`}
                                value={formSettings[setting.settingName] || ''}
                                onChange={(e) => handleSettingChange(setting.settingName, e.target.value)}
                                placeholder={setting.placeholder || ''}
                              />
                              <button
                                type="button"
                                className="password-toggle"
                                onClick={() => togglePasswordVisibility(setting.settingName)}
                              >
                                {showPasswords[setting.settingName] ? <EyeOff className="icon-sm" /> : <Eye className="icon-sm" />}
                              </button>
                            </div>
                          )}

                          {(!setting.dataType || (setting.dataType !== 'SELECT' && setting.dataType !== 'MULTISELECT' && setting.dataType !== 'TEXTAREA' && setting.dataType !== 'PASSWORD')) && (
                            <input
                              type={setting.dataType === 'NUMBER' ? 'number' : 'text'}
                              className={`form-input ${validationErrors[setting.settingName] ? 'error' : ''}`}
                              value={formSettings[setting.settingName] || ''}
                              onChange={(e) => handleSettingChange(setting.settingName, e.target.value)}
                              placeholder={setting.placeholder || ''}
                            />
                          )}

                          {validationErrors[setting.settingName] && (
                            <span className="error-message">{validationErrors[setting.settingName]}</span>
                          )}

                          {setting.helperText && (
                            <span className="form-hint">{setting.helperText}</span>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                );
              })}
            </div>
          </Card>
        )}

        {/* THE ACCOUNTING CODE MAP. Its own screen, not the Conversions one: a code here is a row of ten
            required values plus the property's own working notes, which a source/target editor cannot
            hold. See shared/ConversionCodesPanel. */}
        {isConversionCodeTask && (
          <ConversionCodesPanel syncId={syncId} taskId={taskId} showToast={showToast} />
        )}

        {/* STAY HISTORY: the code values stays went out with unmapped — on the stay load and verify tasks. */}
        {['EXTRACTSTAY', 'VERIFY STAY RECORDS'].includes(String(task.taskName || '').toUpperCase()) && (
          <StayUnmappedCodesPanel syncId={syncId} showToast={showToast} />
        )}

        {/* Conversions Table */}
        {(shouldShowConversions(task.taskName) || shouldShowDefaultValues(task.taskName)) && (
          <Card>
            <div className="card-header">
              <h2>{isDefaultsTask ? 'Default Values' : 'Conversions'}</h2>
              <div className="header-actions">
                <Button variant="secondary" onClick={handleCSVExampleDownload} disabled={conversionDataTypes.length === 0}>
                  <Download className="icon-sm" />
                  Download CSV Example
                </Button>
                <Button variant="secondary" onClick={() => setShowCopyConversionsModal(true)}>
                  <Copy className="icon-sm" />
                  Copy from project
                </Button>
                <Button variant="secondary" onClick={handleCSVDownload} disabled={conversions.length === 0}>
                  <Download className="icon-sm" />
                  Download CSV
                </Button>
                <Button variant="secondary" onClick={() => setShowCSVUploadModal(true)}>
                  <Upload className="icon-sm" />
                  Upload CSV
                </Button>
                <Button onClick={handleAddConversion}>
                  <Plus className="icon-sm" />
                  {isDefaultsTask ? 'Add Default Value' : 'Add Conversion'}
                </Button>
              </div>
            </div>

            {/* Conversion Filters */}
            <div className="conversion-filters">
              <div className="filter-group">
                <label>Record Type</label>
                <select
                  value={filterRecordType}
                  onChange={(e) => {
                    setFilterRecordType(e.target.value);
                    setFilterField('');
                  }}
                  className="form-input"
                >
                  <option value="">All Types</option>
                  {getUniqueRecordTypes.map(([type, label]) => (
                    <option key={type} value={type}>{label}</option>
                  ))}
                </select>
              </div>
              
              {filterRecordType && (
                <div className="filter-group">
                  <label>Field</label>
                  <select
                    value={filterField}
                    onChange={(e) => setFilterField(e.target.value)}
                    className="form-input"
                  >
                    <option value="">All Fields</option>
                    {getFieldsForRecordType.map(([field, label]) => (
                      <option key={field} value={field}>{label}</option>
                    ))}
                  </select>
                </div>
              )}
              
              <div className="filter-group filter-search">
                <label>Search</label>
                <input
                  type="text"
                  value={filterSearch}
                  onChange={(e) => setFilterSearch(e.target.value)}
                  placeholder="Search conversions..."
                  className="form-input"
                />
              </div>
            </div>

            {/* Conversions List */}
            {loadingConversions ? (
              <div className="loading-section">
                <Spinner size="md" />
                <p>Loading conversions...</p>
              </div>
            ) : filteredConversions.length === 0 ? (
              <div className="empty-state">
                <AlertCircle className="empty-icon" />
                <p>{isDefaultsTask ? 'No default values found' : 'No conversions found'}</p>
                <Button onClick={handleAddConversion}>
                  <Plus className="icon-sm" />
                  {isDefaultsTask ? 'Add First Default Value' : 'Add First Conversion'}
                </Button>
              </div>
            ) : (
              <div className="conversions-table-wrapper">
                <table className="conversions-table">
                  <thead>
                    <tr>
                      <th>Record Type</th>
                      <th>Field</th>
                      {!isDefaultsTask && <th>Source Value</th>}
                      <th>{isDefaultsTask ? 'Default Value' : 'Target Value'}</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredConversions.map(conversion => (
                      <tr key={conversion.conversionUuid}>
                        <td>{conversion.recordType}</td>
                        <td>{conversion.sourceFieldName}</td>
                        {!isDefaultsTask && <td>{conversion.sourceValue}</td>}
                        <td>{conversion.targetValue}</td>
                        <td>
                          <div className="table-actions">
                            <button
                              className="action-btn"
                              onClick={() => handleEditConversion(conversion)}
                              title="Edit"
                            >
                              <Edit className="icon-sm" />
                            </button>
                            <button
                              className="action-btn delete"
                              onClick={() => handleDeleteConversion(conversion.conversionUuid)}
                              title="Delete"
                            >
                              <Trash2 className="icon-sm" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Card>
        )}

        {/* Task Summary + Reconciliation — ONE card. */}
        {shouldShowSummary(task.taskName) && (
          <div ref={reconciliationRef}>
          <Card>
            {/* No heading: the page title already names the task and its scope, so "Reconciliation
                (INDIVIDUALS)" restated it directly below. The row is now actions only. */}
            <div className="card-header recon-header">
              <div className="recon-header-actions">
                {user?.isSuperAdmin && (
                  <Button variant="danger" className="sa-only" onClick={() => setShowPurgeConfirm(true)} disabled={purgingRecords || loadingSummary}>
                    <Trash className="icon-sm" />
                    {purgingRecords ? 'Purging...' : 'Purge'}
                  </Button>
                )}
                {user?.isSuperAdmin && (
                  <Button variant="secondary" className="sa-only" onClick={() => setShowReportsModal(true)}>
                    <FileText className="icon-sm" />
                    Reports
                  </Button>
                )}
                <Button variant="secondary" onClick={() => navigate(`/sync/${syncId}/task/${taskId}/analysis`)}>
                  <FileSearch className="icon-sm" />
                  Analyse
                </Button>
                <Button variant="secondary" onClick={handleReconDownloadCSV} disabled={reconDownloading}>
                  <Download className="icon-sm" />
                  {reconDownloading ? 'Downloading...' : 'Download CSV'}
                </Button>
                <Button variant="secondary" onClick={() => setReconShowCSVUploadModal(true)}>
                  <Upload className="icon-sm" />
                  Bulk Upload CSV
                </Button>
                {/* One Refresh for the whole card — it reloads the counts and the records together. */}
                <Button variant="secondary" onClick={refreshSummaryAndRecords} disabled={loadingSummary}>
                  <RefreshCw className="icon-sm" />
                  Refresh
                </Button>
                {user?.isSuperAdmin && (
                  <Button
                    variant="secondary"
                    className="sa-only"
                    onClick={() => setShowRequestModal(true)}
                    title="Request records from the source system by source ID, or from a file of IDs"
                  >
                    <Play className="icon-sm" />
                    Request…
                  </Button>
                )}
                <button
                  type="button"
                  className={`pp-toggle-btn pp-toggle-btn-sm ${autoRefresh ? 'pp-toggle-btn-on' : 'pp-toggle-btn-off'}`}
                  onClick={() => setAutoRefresh(v => !v)}
                  title={autoRefresh ? 'Auto-refresh is on — counts and records reload every 3s' : 'Turn on to reload counts and records every 3s'}
                >
                  <span className="pp-toggle-track"><span className="pp-toggle-thumb" /></span>
                  <span className="pp-toggle-label">Auto-refresh 3s</span>
                </button>
              </div>
            </div>

            {loadingSummary ? (
              <div className="loading-section">
                <Spinner size="md" />
                <p>Loading summary...</p>
              </div>
            ) : taskSummary.length === 0 ? (
              <div className="empty-state">
                <AlertCircle className="empty-icon" />
                <p>No summary data available</p>
              </div>
            ) : (
              <TaskSummaryStats summary={taskSummary} onScopeClick={handleReconScopeClick} />
            )}

            {/* Reconciliation — same card as the summary above it. These were two stacked cards, each
                with its own heading, its own Refresh and its own auto-refresh toggle, which cost most
                of a screen before a single record was visible. The counts and the records are one
                subject, so they now share one header, one refresh and one filter bar. */}
            <div className="recon-divider" />

            {reconError && (
              <div className="error-alert">
                <div className="error-alert-content">
                  <AlertCircle className="error-alert-icon" />
                  <div>
                    <h3 className="error-alert-title">Search Failed</h3>
                    <p className="error-alert-message">{reconError}</p>
                  </div>
                </div>
              </div>
            )}

            <div className="filters-grid">
              {/* Scope Type is fixed per task (derived server-side from the task), so it's not shown
                  as a filter — it would just take up space with a single read-only value. */}
              {/* No visible labels on these two: the select's own "All Statuses" option and the input's
                  placeholder already say what each one is, so the labels only added a row of height.
                  aria-label keeps them named for screen readers. */}
              <div className="filter-field">
                <select
                  value={reconRecordStatus}
                  onChange={e => setReconRecordStatus(e.target.value)}
                  className="filter-input"
                  aria-label="Record status"
                >
                  {/* IN-FLIGHT matches the summary's In-Flight column: every record not in a terminal
                      state, not the literal IN-FLIGHT status. QUEUED / REQUESTED / PROCESSING / COMPLETE
                      were removed — nothing writes them to the SPR store, so they always returned
                      nothing, which is how a red in-flight count came to look unsearchable. */}
                  {RECORD_STATUS_OPTIONS.map(o => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                  ))}
                </select>
              </div>
              <div className="filter-field filter-field-wide">
                <input
                  type="text"
                  value={reconSearch}
                  onChange={e => setReconSearch(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && fetchReconRecords()}
                  placeholder="Search source ID, target ID or comment..."
                  className="filter-input"
                  aria-label="Search source ID, target ID or comment"
                />
              </div>
            </div>

            <div className="filter-checkbox-section">
              <label className="filter-checkbox-label">
                <input
                  type="checkbox"
                  checked={reconShowOnlyNullTargetId}
                  onChange={() => setReconShowOnlyNullTargetId(v => !v)}
                  className="checkbox-input"
                />
                <span>Show only records with no target record ID</span>
              </label>
            </div>

            <div className="filters-buttons">
              <Button variant="primary" onClick={() => fetchReconRecords()} disabled={reconLoading}>
                {reconLoading ? <><Spinner size="sm" className="icon-sm" />Searching...</> : <><Search className="icon-sm" />Search</>}
              </Button>
              <Button variant="secondary" onClick={handleReconReset} disabled={reconLoading}>
                <RefreshCw className="icon-sm" />
                Reset
              </Button>

              {/* Bulk actions, right-aligned on the same row as Search/Reset. They were a separate
                  "selection bar" strip between the filters and the table, which spent a full row on
                  four buttons that are disabled most of the time. The selected/total count moved with
                  them — it is the label that makes "Selected" mean something. */}
              {reconTotalRecords > 0 && (
                <div className="filters-buttons-right">
                  <span className={`selection-text ${reconSelectedRecords.size > 0 ? 'selection-text-active' : ''}`}>
                    {reconSelectedRecords.size} of {reconTotalRecords} selected
                  </span>
                  <Button variant="secondary" onClick={() => handleReconDeleteClick(reconDeletable)} disabled={reconDeleting || reconDeletable.length === 0}>
                    <Trash2 className="icon-sm" />
                    Delete Selected
                  </Button>
                  <Button variant="secondary" onClick={() => handleReconBulkAction('skip')} disabled={reconProcessingBulkAction || reconSelectedRecords.size === 0}>
                    <SkipForward className="icon-sm" />
                    Skip Selected
                  </Button>
                  <Button variant="primary" onClick={() => handleReconBulkAction('reprocess')} disabled={reconProcessingBulkAction || reconSelectedRecords.size === 0}>
                    <Play className="icon-sm" />
                    Reprocess Selected
                  </Button>
                  {user?.isSuperAdmin && (reconReprocessAllConfirm ? (
                    // Confirmed inline (no modal): the question names exactly what will be actioned.
                    <span className="sa-only" style={{ display: 'inline-flex', alignItems: 'center', gap: '0.5rem' }}>
                      <span className="selection-text selection-text-active">
                        Reprocess every {reconRecordStatus} record{reconSearch ? ` matching "${reconSearch}"` : ''}?
                      </span>
                      <Button variant="primary" onClick={handleReconReprocessAllInStatus}>
                        <Play className="icon-sm" />
                        Start
                      </Button>
                      <Button variant="secondary" onClick={() => setReconReprocessAllConfirm(false)}>Cancel</Button>
                    </span>
                  ) : (
                    <Button
                      variant="primary"
                      className="sa-only"
                      onClick={() => setReconReprocessAllConfirm(true)}
                      disabled={reconProcessingBulkAction || reconReprocessAllStarting || reconBulkRunning || !reconRecordStatus}
                      title={!reconRecordStatus ? 'Choose a Record Status first'
                        : reconBulkRunning ? 'A reprocess of this task is already running'
                        : `Reprocess every ${reconRecordStatus} record in this task, on the server`}
                    >
                      <Play className="icon-sm" />
                      {reconReprocessAllStarting ? 'Starting…' : 'Reprocess all in this status'}
                    </Button>
                  ))}
                </div>
              )}
            </div>
            {user?.isSuperAdmin && reconBulkJob && (
              <div className="sa-only" style={{
                display: 'flex', alignItems: 'center', gap: '0.75rem', marginTop: '0.75rem', padding: '0.625rem 0.875rem',
                borderRadius: 6, fontSize: '0.875rem',
                background: reconBulkJob.status === 'FAILED' ? '#fef2f2' : reconBulkRunning ? '#eff6ff' : '#f0fdf4',
                color: reconBulkJob.status === 'FAILED' ? '#991b1b' : reconBulkRunning ? '#1e40af' : '#166534',
              }}>
                {reconBulkRunning && <Spinner size="sm" className="icon-sm" />}
                <span style={{ flex: 1 }}>{reconBulkJobText(reconBulkJob)}</span>
                {!reconBulkRunning && (
                  <Button variant="secondary" onClick={() => setReconBulkJob(null)}>Dismiss</Button>
                )}
              </div>
            )}
          </Card>

          <Card className="table-card">
            <div className="table-wrapper">
              <table className="records-table">
                <thead>
                  <tr>
                    <th className="checkbox-cell">
                      <input type="checkbox" checked={reconAllPageSelected} onChange={toggleReconSelectAll} className="checkbox-input" aria-label="Select all" />
                    </th>
                    <th className="sortable-header" onClick={() => handleReconSort('sourcerecordid')}>Source Record ID {renderReconSortIcon('sourcerecordid')}</th>
                    <th className="sortable-header" onClick={() => handleReconSort('targetRecordId')}>Target Record ID {renderReconSortIcon('targetRecordId')}</th>
                    <th className="sortable-header" onClick={() => handleReconSort('recordStatus')}>Status {renderReconSortIcon('recordStatus')}</th>
                    <th className="sortable-header" onClick={() => handleReconSort('comment')}>Comment {renderReconSortIcon('comment')}</th>
                    <th className="sortable-header" onClick={() => handleReconSort('lastSyncTime')}>Last Sync Time {renderReconSortIcon('lastSyncTime')}</th>
                    <th className="action-header">Logs</th>
                  </tr>
                </thead>
                <tbody>
                  {reconPaginatedRecords.length === 0 && reconLoading ? (
                    /* The search now runs on arrival, so without this the table would show "Ready to
                       Search" — or "No records found" — for the length of that first query, which reads
                       as a result rather than as work in progress. */
                    <tr>
                      <td colSpan={7} className="empty-cell">
                        <Spinner size="sm" className="icon-sm" />
                        <p className="empty-message">Loading records…</p>
                      </td>
                    </tr>
                  ) : reconPaginatedRecords.length === 0 ? (
                    <tr>
                      <td colSpan={7} className="empty-cell">
                        <AlertCircle className="empty-icon" />
                        {!reconHasSearched ? (
                          <>
                            <p className="empty-title">Ready to Search</p>
                            <p className="empty-subtitle">Configure search criteria above and click Search, or click a summary row above to filter automatically</p>
                          </>
                        ) : (
                          <>
                            <p className="empty-message">No records found matching your search criteria</p>
                            <Button variant="secondary" onClick={handleReconReset} className="empty-button">Clear Filters</Button>
                          </>
                        )}
                      </td>
                    </tr>
                  ) : (
                    reconPaginatedRecords.map((record, index) => {
                      const recordIdKey = record.recordid?.trim() || null;
                      const hasValidRecordId = Boolean(recordIdKey);
                      const isSelected = hasValidRecordId && reconSelectedRecords.has(recordIdKey);
                      const rowKey = record.sprRecordUuid || record.sourcerecordid || index;
                      return (
                        <React.Fragment key={rowKey}>
                        <tr
                          className={`record-row ${isSelected ? 'record-row-selected' : ''}`}
                        >
                          <td className="checkbox-cell">
                            <input type="checkbox" className="checkbox-input" checked={isSelected} onChange={() => recordIdKey && toggleReconSelectRecord(recordIdKey)} disabled={!hasValidRecordId} aria-label={`Select ${record.sourcerecordid}`} />
                          </td>
                          <td className="record-cell">
                            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                              {record.sourcerecordid || '-'}
                              {user?.isSuperAdmin && record.sourcerecordid && (
                                <button
                                  title="Search CloudWatch logs for this record"
                                  onClick={(e) => openCwLogsForRecord(e, record.sourcerecordid)}
                                  style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', padding: 3, border: '1px solid #93c5fd', borderRadius: 4, background: '#eff6ff', color: '#1d4ed8', cursor: 'pointer' }}
                                >
                                  <FileSearch size={14} />
                                </button>
                              )}
                            </span>
                          </td>
                          <td className="record-cell">
                            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                              {record.targetRecordId || '-'}
                              {user?.isSuperAdmin && record.targetRecordId && (
                                <button
                                  title="Search CloudWatch logs for this record"
                                  onClick={(e) => openCwLogsForRecord(e, record.targetRecordId)}
                                  style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', padding: 3, border: '1px solid #93c5fd', borderRadius: 4, background: '#eff6ff', color: '#1d4ed8', cursor: 'pointer' }}
                                >
                                  <FileSearch size={14} />
                                </button>
                              )}
                            </span>
                          </td>
                          <td className="status-cell">
                            <span className={`status-badge ${getReconStatusColor(record.recordStatus)}`} title={getRecordStatusTitle(record.recordStatus)}>{record.recordStatus || '-'}</span>
                            {user?.isSuperAdmin && record.jiraKey && (
                              <a
                                className="recon-jira-chip sa-only"
                                href={`https://hconnect.atlassian.net/browse/${record.jiraKey}`}
                                target="_blank"
                                rel="noreferrer"
                                title="Jira ticket for this record"
                                onClick={(e) => e.stopPropagation()}
                              >
                                {record.jiraKey}
                              </a>
                            )}
                          </td>
                          <td className="error-cell">
                            {renderStoryComment(record)}
                            {hasStoredFile(record) && (
                              <a
                                href="#link-to-file"
                                className="recon-file-link"
                                onClick={(e) => openRecordFile(e, record)}
                                title="Download the stored file. A new link is created when you click, valid for 5 minutes."
                              >
                                <Download size={13} />
                                {fileLinkBusy === record.sprRecordUuid ? 'Creating link…' : 'Link to file'}
                              </a>
                            )}
                            {record.excelKey && (
                              <a
                                href="#link-to-excel"
                                className="recon-file-link"
                                onClick={(e) => openRecordFile(e, record, 'excel')}
                                title="Download the same data as an Excel workbook (not sent to SFTP). A new link is created when you click, valid for 5 minutes."
                              >
                                <Download size={13} />
                                {fileLinkBusy === `${record.sprRecordUuid}:excel` ? 'Creating link…' : 'Link to Excel'}
                              </a>
                            )}
                          </td>
                          <td className="date-cell">
                            {record.lastSyncTime ? (
                              <div style={{ display: 'flex', flexDirection: 'column', lineHeight: 1.3 }}>
                                <span>{new Date(record.lastSyncTime).toLocaleDateString()}</span>
                                <span style={{ color: '#6b7280', fontSize: '0.8rem' }}>{new Date(record.lastSyncTime).toLocaleTimeString()}</span>
                              </div>
                            ) : '-'}
                          </td>
                          <td className="action-cell">
                            <div style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                              {user?.isSuperAdmin && record.sprRecordUuid && (
                                <button
                                  className="icon-button sa-only"
                                  title="View message log & status history"
                                  onClick={() => handleReconRowClick(record)}
                                >
                                  <FileText size={16} />
                                </button>
                              )}
                              {/* THE SAME QUESTION, ANSWERED WITHIN THE PERMISSIONS THEY HAVE. A
                                  superadmin opens the whole message log; everyone else gets the one
                                  call that failed. Only on a failed record — on a successful one there
                                  is no failing request, and the button would be a promise the overlay
                                  cannot keep. */}
                              {!user?.isSuperAdmin && record.sprRecordUuid && isFailedRecord(record) && (
                                <button
                                  className="icon-button"
                                  title="View the request and response for this failure"
                                  onClick={(e) => { e.stopPropagation(); openFailedExchange(record); }}
                                >
                                  <FileSearch size={16} />
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                        </React.Fragment>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>

            {reconNextContinuationToken && (
              <div className="load-more-container">
                <Button variant="secondary" onClick={handleReconLoadMore} disabled={reconLoadingMore}>
                  {reconLoadingMore ? 'Loading...' : `Load More (${reconRecords.length} loaded)`}
                </Button>
              </div>
            )}

            {reconTotalPages > 1 && (
              <div className="pagination">
                <div className="pagination-info">
                  Showing {(reconPage - 1) * reconPageSize + 1} to {Math.min(reconPage * reconPageSize, reconTotalRecords)} of {reconTotalRecords} results
                </div>
                <div className="pagination-controls">
                  <Button variant="secondary" onClick={() => setReconPage(p => Math.max(1, p - 1))} disabled={reconPage === 1}>Previous</Button>
                  <div className="pagination-page">Page {reconPage} of {reconTotalPages}</div>
                  <Button variant="secondary" onClick={() => setReconPage(p => Math.min(reconTotalPages, p + 1))} disabled={reconPage === reconTotalPages}>Next</Button>
                </div>
              </div>
            )}
          </Card>
        </div>
        )}

        {/* Actions */}
        {task.actions && task.actions.length > 0 && (
          <Card>
            <div className="card-header">
              <h2>Available Actions</h2>
            </div>
            <div className="actions-list">
              {task.actions.map(action => (
                <div key={action.actionName} className="action-item">
                  <div className="action-info">
                    <h4>{action.actionLabel || action.actionName}</h4>
                    {action.actionDescription && (
                      <p>{action.actionDescription}</p>
                    )}
                  </div>
                  <Button
                    onClick={() => handleExecuteAction(action.actionName)}
                    disabled={executingAction === action.actionName}
                  >
                    {executingAction === action.actionName ? (
                      <>
                        <Spinner size="sm" className="icon-sm" />
                        Executing...
                      </>
                    ) : (
                      <>
                        <Play className="icon-sm" />
                        Execute
                      </>
                    )}
                  </Button>
                </div>
              ))}
            </div>
          </Card>
        )}
      </div>

      {/* Error Knowledge Base modal (view for anyone, add/edit for superadmins) */}
      {kbModalElement}

      {/* The failing request, for a user who cannot open the message log. A JSON viewer overlay, which
          is the one thing STYLE.md exempts from "detail views are pages" — it carries no form and no
          table, so there is nothing here to link to or come back to. */}
      {exchangeView && (
        <ExchangeViewerOverlay
          title="Failed request"
          subtitle={[exchangeView.record?.sourcerecordid, exchangeView.record?.targetRecordId]
            .filter(Boolean).join('  \u2192  ')}
          exchange={exchangeView.exchange}
          loading={exchangeView.loading}
          error={exchangeView.error}
          onClose={() => setExchangeView(null)}
        />
      )}

      {/* Sticky Footer — only on editable CONFIG tasks (source/target/conversions/setup). Extraction
          tasks show the summary/reconciliation; PROCESS/SCHEDULE tasks are triggered by their own
          action button (and save any settings via the Task Settings card) — neither has a
          "Save Configuration" to show here. */}
      {!shouldShowSummary(task.taskName) && !isProcessOrScheduleTask() && (
        <div className="sticky-footer">
          <div className="footer-content">
            <Button variant="secondary" onClick={handleCancel} disabled={saving}>
              <X className="icon-sm" />
              Cancel
            </Button>
            {/* NO SAVE ON CONVERSIONS / DEFAULT VALUES. There is nothing on those screens for it to
                save — each conversion is written the moment it is added, edited or uploaded — and the
                button was actively destructive: handleSave resolved the connector role as "not
                CONFIGURE SOURCE, therefore TARGET" and wrote the target connector's settings, unbinding
                its inherited credential. The server refuses that now and handleSave returns early, but
                an affordance that does nothing is still the wrong thing to offer after someone has just
                added a conversion. Cancel stays — leaving the screen is still a real action. */}
            {!shouldShowConversions(task.taskName) && !shouldShowDefaultValues(task.taskName) && (
            <Button onClick={handleSave} disabled={saving}>
              {saving ? (
                <>
                  <Spinner size="sm" className="icon-sm" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="icon-sm" />
                  Save Configuration
                </>
              )}
            </Button>
            )}
          </div>
        </div>
      )}

      {/* Toast Notification */}
      {toast && (
        <div className={`toast toast-${toast.type}`}>
          <div className="toast-content">
            {toast.type === 'success' && <CheckCircle className="toast-icon" />}
            {toast.type === 'error' && <AlertCircle className="toast-icon" />}
            <span>{toast.message}</span>
          </div>
          <button className="toast-close" onClick={() => setToast(null)}>
            <X className="icon-sm" />
          </button>
        </div>
      )}

      {/* Modals */}
      {showConversionModal && (
        <ConversionEditModal
          isOpen={showConversionModal}
          onClose={() => {
            setShowConversionModal(false);
            setEditingConversion(null);
          }}
          onSave={handleConversionSave}
          conversion={editingConversion}
          conversionDataTypes={conversionDataTypes}
          isDefault={isDefaultsTask}
        />
      )}

      {showCSVUploadModal && (
        <ConversionCSVUploadModal
          isOpen={showCSVUploadModal}
          onClose={() => setShowCSVUploadModal(false)}
          onUpload={handleCSVUpload}
          isDefault={isDefaultsTask}
        />
      )}

      {showCopyConversionsModal && (
        <CopyConversionsModal
          accountId={project?.projectDetails?.accountId}
          currentSyncUuid={syncId}
          templateName={project?.projectDetails?.templateName}
          currentConversions={conversions}
          isDefault={isDefaultsTask}
          onConfirm={handleCopyConversions}
          onClose={() => setShowCopyConversionsModal(false)}
        />
      )}

      {showCopySettingsModal && (
        <CopySettingsModal
          accountId={project?.projectDetails?.accountId}
          currentSyncUuid={syncId}
          templateName={project?.projectDetails?.templateName}
          taskName={task.taskName}
          settings={task.taskSettings || []}
          values={formSettings}
          onApply={handleCopySettings}
          onClose={() => setShowCopySettingsModal(false)}
        />
      )}

      {showActionConfirm && (
        <ActionConfirmModal
          isOpen={!!showActionConfirm}
          onClose={() => setShowActionConfirm(null)}
          onConfirm={handleConfirmAction}
          actionName={showActionConfirm}
        />
      )}

      {showSingleFileConfirm && (
        <ConfirmModal
          title="Single-file export"
          message="This is a single-file export of all the data. All previous data for this task will be deleted and replaced with a new upload from the source."
          confirmLabel="Send & Replace"
          danger
          onConfirm={() => { setShowSingleFileConfirm(false); doSendInstruction(); }}
          onCancel={() => setShowSingleFileConfirm(false)}
        />
      )}

      {showProcessConfirm && (
        // The run confirmation is data-driven: the task's `processWarning` field (editable in the admin
        // task form) holds a destructive-action warning (e.g. the PEP purge/overwrite message). When set,
        // it's shown as a danger confirm; when blank, a plain "Run this process?" confirmation.
        task.processWarning ? (
          <ConfirmModal
            title="Run process"
            message={task.processWarning}
            confirmLabel="Confirm & Process"
            danger
            onConfirm={() => { setShowProcessConfirm(false); handleInvokeProcess(); }}
            onCancel={() => setShowProcessConfirm(false)}
          />
        ) : (
          <ConfirmModal
            title="Run process"
            message={`Run "${task.defaultProcess?.name || task.taskName}" now?`}
            confirmLabel="Run Process"
            onConfirm={() => { setShowProcessConfirm(false); handleInvokeProcess(); }}
            onCancel={() => setShowProcessConfirm(false)}
          />
        )
      )}

      {/* Run Process with step settings — prompt for the process step's settings (e.g. START_DATE/
          END_DATE) before running. Shown when the process's step declares displayable settings. */}
      {showProcessSettings && (
        <ConfirmModal
          title={`Run "${task.defaultProcess?.name || task.taskName}"`}
          message={task.processWarning || 'Enter the values for this run:'}
          confirmLabel={invokingProcess ? 'Running…' : 'Run Process'}
          danger={!!task.processWarning}
          onConfirm={handleRunWithSettings}
          onCancel={() => { setShowProcessSettings(false); setProcessSettingsError(null); }}
        >
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14, margin: '4px 0 2px' }}>
            {(task.defaultProcess?.settings || []).map((s) => {
              const val = processSettingValues[s.settingName] ?? '';
              const onChange = (e) => setProcessSettingValues((prev) => ({ ...prev, [s.settingName]: e.target.value }));
              const dt = (s.dataType || 'TEXT').toUpperCase();
              const inputType = dt === 'DATE' ? 'date' : dt === 'NUMBER' ? 'number' : dt === 'PASSWORD' ? 'password' : 'text';
              return (
                <div key={s.settingName} style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                  <label style={{ fontSize: '0.8rem', fontWeight: 600, color: '#374151' }}>
                    {s.settingLabel || s.settingName}{s.required === 'Y' && <span style={{ color: '#dc2626' }}> *</span>}
                  </label>
                  {dt === 'SELECT' ? (
                    <select className="form-control" value={val} onChange={onChange}>
                      <option value="">— select —</option>
                      {String(s.availableValues || '').split('|').filter(Boolean).map((opt) => {
                        const [v, label] = opt.split(':');
                        return <option key={v} value={v}>{label || v}</option>;
                      })}
                    </select>
                  ) : dt === 'CHECKBOX' ? (
                    <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: '0.85rem', color: '#374151' }}>
                      <input type="checkbox" checked={val === 'Y'}
                        onChange={(e) => setProcessSettingValues((prev) => ({ ...prev, [s.settingName]: e.target.checked ? 'Y' : 'N' }))} />
                      {s.placeholder || 'Yes'}
                    </label>
                  ) : (
                    <input className="form-control" type={inputType} value={val} placeholder={s.placeholder || ''} onChange={onChange} />
                  )}
                  {s.helperText && <div style={{ fontSize: '0.75rem', color: '#6b7280' }}>{s.helperText}</div>}
                </div>
              );
            })}
            {processSettingsError && <div style={{ color: '#dc2626', fontSize: '0.8rem' }}>{processSettingsError}</div>}
          </div>
        </ConfirmModal>
      )}

      <PurgeConfirmModal
        isOpen={showPurgeConfirm}
        onClose={() => setShowPurgeConfirm(false)}
        onConfirm={handlePurgeRecords}
      />

      {showReportsModal && (
        <ReportsModal
          isOpen={showReportsModal}
          onClose={() => setShowReportsModal(false)}
          reports={[
            { name: 'Overview', description: 'Reconciliation summary, errors and notes', path: `/sync/${syncId}/task/${taskId}/report` },
            { name: 'On-site instructions', description: 'Instructions to action on-site', path: `/sync/${syncId}/task/${taskId}/onsite` },
            { name: 'Onsite instructions without warnings', description: 'Errors only — no warnings', path: `/sync/${syncId}/task/${taskId}/onsite-errors` },
          ]}
          onOpen={(r) => { setShowReportsModal(false); navigate(r.path); }}
        />
      )}

      {/* Reconciliation modals */}
      {reconProfileWizardItems && (
        <ReprocessProfilesModal
          items={reconProfileWizardItems}
          otherCount={reconSelectedRecords.size - reconProfileWizardItems.length}
          processing={reconProcessingBulkAction}
          onCancel={() => { setReconProfileWizardItems(null); setReconPendingAction(null); }}
          onConfirm={(profileIds) => handleReconConfirmAction(profileIds)}
        />
      )}

      {reconShowConfirmModal && (
        <div className="modal-overlay" onClick={() => setReconShowConfirmModal(false)}>
          <div className="modal-content modal-sm" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Confirm Bulk Action</h2>
              <button className="modal-close" onClick={() => setReconShowConfirmModal(false)}>×</button>
            </div>
            <div className="modal-body">
              <p>Are you sure you want to <strong>{reconPendingAction}</strong> {reconSelectedRecords.size} record(s)?</p>
              <p className="text-muted">This action cannot be undone.</p>
            </div>
            <div className="modal-footer">
              <Button variant="secondary" onClick={() => setReconShowConfirmModal(false)}>Cancel</Button>
              <Button onClick={() => handleReconConfirmAction()}>{reconPendingAction === 'skip' ? 'Skip Records' : 'Reprocess Records'}</Button>
            </div>
          </div>
        </div>
      )}

      {reconBulkActionResult && reconShowSuccessModal && (
        <div className="modal-overlay" onClick={() => { setReconShowSuccessModal(false); setReconBulkActionResult(null); }}>
          <div className="modal-content modal-sm" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Action Successful</h2>
              <button className="modal-close" onClick={() => { setReconShowSuccessModal(false); setReconBulkActionResult(null); }}>×</button>
            </div>
            <div className="modal-body">
              <p>Successfully {reconBulkActionResult.actionType === 'skip' ? 'skipped' : 'reprocessed'} {reconBulkActionResult.count} record(s).</p>
              {reconBulkActionResult.profilesRequested > 0 && (
                <p style={{ color: '#2563eb', marginTop: 8 }}>
                  {reconBulkActionResult.profilesRequested} missing profile(s) were re-requested and will sync first.
                </p>
              )}
              {reconBulkActionResult.skippedNoSource > 0 && (
                <p style={{ color: '#b45309', marginTop: 8 }}>
                  {reconBulkActionResult.skippedNoSource} record(s) were skipped because they have no source record ID to reprocess.
                </p>
              )}
              {reconBulkActionResult.skippedAccepted > 0 && (
                <p style={{ color: '#b45309', marginTop: 8 }}>
                  {reconBulkActionResult.skippedAccepted} stay record(s) were not re-sent because OPERA Cloud already accepted them — a second send would duplicate the stay.
                </p>
              )}
            </div>
            <div className="modal-footer">
              <Button onClick={() => { setReconShowSuccessModal(false); setReconBulkActionResult(null); }}>Close</Button>
            </div>
          </div>
        </div>
      )}

      {reconShowDeleteModal && (
        <div className="modal-overlay" onClick={() => setReconShowDeleteModal(false)}>
          <div className="modal-content modal-sm" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Confirm Delete</h2>
              <button className="modal-close" onClick={() => setReconShowDeleteModal(false)}>×</button>
            </div>
            <div className="modal-body">
              <p>Are you sure you want to delete {reconRecordsToDelete.length} record(s)?</p>
              <p className="text-muted">This action cannot be undone.</p>
            </div>
            <div className="modal-footer">
              <Button variant="secondary" onClick={() => setReconShowDeleteModal(false)} disabled={reconDeleting}>Cancel</Button>
              <Button onClick={handleReconDeleteConfirm} disabled={reconDeleting}>
                {reconDeleting ? <><Spinner size="sm" className="icon-sm" />Deleting...</> : <><Trash2 className="icon-sm" />Delete Records</>}
              </Button>
            </div>
          </div>
        </div>
      )}

      {reconShowCSVUploadModal && (
        <ReconCSVUploadModal
          isOpen={reconShowCSVUploadModal}
          onClose={() => setReconShowCSVUploadModal(false)}
          syncId={syncId}
          taskId={taskId}
          onUploadSuccess={handleReconCSVUploadSuccess}
        />
      )}

      {/* Request records from the source system — superadmin. In a modal rather than inline under the
          filters: four controls for an occasional action, which inline pushed the record list itself
          down the page on every visit. */}
      <RequestSourceModal
        isOpen={showRequestModal}
        onClose={() => setShowRequestModal(false)}
        scope={reqScope || reconScopeType || ''}
        onScopeChange={setReqScope}
        sourceId={reqSourceId}
        onSourceIdChange={setReqSourceId}
        submitting={reqSubmitting}
        onSubmit={handleRequestSource}
        onFromFile={() => { setShowRequestModal(false); setShowBulkRequestModal(true); }}
      />

      {showBulkRequestModal && (
        <BulkRequestSourceModal
          isOpen={showBulkRequestModal}
          onClose={() => setShowBulkRequestModal(false)}
          syncId={syncId}
          taskId={taskId}
          defaultScope={reqScope || reconScopeType || ''}
          onComplete={(summary) => {
            showToast(summary, 'success');
            fetchReconRecords();
          }}
        />
      )}

    </div>
  );
}

function ReconCSVUploadModal({ isOpen, onClose, syncId, taskId, onUploadSuccess }) {
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!file) return;
    try {
      setUploading(true);
      setError(null);
      await syncService.uploadReconciliationCSV(syncId, taskId, file);
      onUploadSuccess();
      onClose();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to upload CSV');
    } finally {
      setUploading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Upload Reconciliation CSV</h2>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="modal-body">
            {error && <div className="error-banner"><AlertCircle className="icon-sm" />{error}</div>}
            <div className="form-group">
              <label className="form-label">CSV File *</label>
              <input type="file" accept=".csv" className="form-input" onChange={e => setFile(e.target.files[0])} required />
              <span className="form-hint">CSV should include reconciliation record data</span>
            </div>
          </div>
          <div className="modal-footer">
            <Button variant="secondary" onClick={onClose} type="button" disabled={uploading}>Cancel</Button>
            <Button type="submit" disabled={uploading || !file}>
              {uploading ? <><Spinner size="sm" className="icon-sm" />Uploading...</> : <><Upload className="icon-sm" />Upload</>}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}



// Superadmin: bulk "request by source id" from a file. Upload one source id per line, or a CSV whose
// header names the id column, and request every id under the chosen scope. Reuses the SAME single
// requestSource endpoint per id (client-side, bounded concurrency) so any list size works without
// hitting the API Gateway 29s limit, and every row shows its own result. Mirrors ReconCSVUploadModal.
/**
 * Superadmin: request records from the SOURCE system, by a single source id or from a file of ids.
 *
 * These four controls used to sit inline beneath the reconciliation filters, taking a full row on every
 * visit for an action used occasionally by one role. The modal keeps them one click away without
 * spending permanent vertical space on them.
 *
 * Field state stays in the PAGE, not here — the scope defaults to the task's own scope, and the bulk
 * upload modal reads the same value, so the two entry points cannot disagree about what is being
 * requested.
 */
function RequestSourceModal({ isOpen, onClose, scope, onScopeChange, sourceId, onSourceIdChange, submitting, onSubmit, onFromFile }) {
  if (!isOpen) return null;
  const canSubmit = !submitting && sourceId.trim() && scope;
  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Request Records</h2>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>
        <div className="modal-body">
          <p className="modal-intro">
            Ask the source system to send a record again. It re-enters the pipeline as if it had just
            changed, so it is the way to pull in something that was never synced or to refresh one that
            is stale.
          </p>
          <div className="form-group">
            <label className="filter-label">Scope</label>
            <select
              className="form-input"
              value={scope}
              onChange={e => onScopeChange(e.target.value)}
            >
              <option value="">Scope…</option>
              {REQUEST_SCOPE_OPTIONS.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
          <div className="form-group">
            <label className="filter-label">Source ID</label>
            <input
              className="form-input"
              placeholder="Source ID"
              value={sourceId}
              onChange={e => onSourceIdChange(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter' && canSubmit) { e.preventDefault(); onSubmit(); } }}
              autoFocus
            />
          </div>
        </div>
        <div className="modal-footer modal-footer-split">
          <Button variant="secondary" onClick={onFromFile} type="button"
            title="Upload a file of source IDs and request them all under the chosen scope">
            <Upload className="icon-sm" />
            Request from file…
          </Button>
          <div className="modal-footer-right">
            <Button variant="secondary" onClick={onClose} type="button">Cancel</Button>
            <Button variant="primary" onClick={onSubmit} disabled={!canSubmit} type="button">
              {submitting ? <><Spinner size="sm" className="icon-sm" />Requesting…</> : <><Play className="icon-sm" />Request by source ID</>}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

function BulkRequestSourceModal({ isOpen, onClose, syncId, taskId, defaultScope, onComplete }) {
  const CONCURRENCY = 5;
  const [fileName, setFileName] = useState('');
  const [ids, setIds] = useState([]);            // parsed, de-duplicated source ids (order preserved)
  const [columnUsed, setColumnUsed] = useState('');
  const [scope, setScope] = useState(defaultScope || '');
  const [statuses, setStatuses] = useState({});  // { [id]: { state: 'pending'|'ok'|'error', message? } }
  const [running, setRunning] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState(null);

  // A plain list (one id per line) OR a CSV where a header names the id column — pick that column,
  // else the first. De-duplicate, preserve order, strip a header row and surrounding quotes.
  const parseSourceIds = (text) => {
    const lines = text.split(/\r?\n/).map(l => l.trim()).filter(Boolean);
    if (!lines.length) return { ids: [], column: '' };
    const headerRe = /^(source[_ ]?record[_ ]?id|source[_ ]?reservation[_ ]?id|source[_ ]?id|id)$/i;
    const isCsv = lines[0].includes(',');
    let colIndex = 0, column = '', dataLines = lines;
    if (isCsv) {
      const header = lines[0].split(',').map(h => h.trim().replace(/^"|"$/g, ''));
      const found = header.findIndex(h => headerRe.test(h));
      colIndex = found >= 0 ? found : 0;
      column = header[colIndex];
      dataLines = lines.slice(1);
    } else if (headerRe.test(lines[0])) {
      column = lines[0];
      dataLines = lines.slice(1);
    }
    const seen = new Set(), out = [];
    for (const line of dataLines) {
      const cell = (isCsv ? (line.split(',')[colIndex] || '') : line).trim().replace(/^"|"$/g, '');
      if (cell && !seen.has(cell)) { seen.add(cell); out.push(cell); }
    }
    return { ids: out, column };
  };

  const handleFile = (e) => {
    const f = e.target.files[0];
    if (!f) return;
    setError(null); setDone(false); setStatuses({}); setFileName(f.name);
    const reader = new FileReader();
    reader.onload = () => {
      const { ids: parsed, column } = parseSourceIds(String(reader.result || ''));
      setIds(parsed);
      setColumnUsed(column);
      if (!parsed.length) setError('No source IDs found in the file');
    };
    reader.onerror = () => setError('Could not read the file');
    reader.readAsText(f);
  };

  const handleSubmit = async () => {
    if (!ids.length || !scope || running) return;
    setRunning(true); setDone(false); setError(null);
    setStatuses(Object.fromEntries(ids.map(id => [id, { state: 'pending' }])));
    let ok = 0, failed = 0, idx = 0;
    const worker = async () => {
      while (idx < ids.length) {
        const id = ids[idx++];
        try {
          await syncService.requestSource(syncId, taskId, id, scope);
          ok++;
          setStatuses(s => ({ ...s, [id]: { state: 'ok' } }));
        } catch (err) {
          failed++;
          setStatuses(s => ({ ...s, [id]: { state: 'error', message: err.response?.data?.message || err.message || 'failed' } }));
        }
      }
    };
    await Promise.all(Array.from({ length: Math.min(CONCURRENCY, ids.length) }, worker));
    setRunning(false); setDone(true);
    if (onComplete) onComplete(`Requested ${ok} of ${ids.length} source record(s)${failed ? `, ${failed} failed` : ''} (${scope})`);
  };

  if (!isOpen) return null;

  const processed = Object.values(statuses).filter(s => s.state === 'ok' || s.state === 'error').length;

  return (
    <div className="modal-overlay" onClick={running ? undefined : onClose}>
      <div className="modal-content" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Request by Source ID from File</h2>
          <button className="modal-close" onClick={onClose} disabled={running}>×</button>
        </div>
        <div className="modal-body">
          {error && <div className="error-banner"><AlertCircle className="icon-sm" />{error}</div>}

          <div className="form-group">
            <label className="form-label">Scope *</label>
            <select className="form-input" value={scope} onChange={e => setScope(e.target.value)} disabled={running}>
              <option value="">Scope…</option>
              {REQUEST_SCOPE_OPTIONS.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
            <span className="form-hint">Every source ID in the file is requested under this scope.</span>
          </div>

          <div className="form-group">
            <label className="form-label">File *</label>
            <input type="file" accept=".csv,.txt" className="form-input" onChange={handleFile} disabled={running} />
            <span className="form-hint">One source ID per line, or a CSV whose header names the ID column.</span>
          </div>

          {ids.length > 0 && (
            <div className="form-group">
              <label className="form-label">
                {ids.length} source ID{ids.length !== 1 ? 's' : ''}{columnUsed ? ` (column “${columnUsed}”)` : ''}{fileName ? ` from ${fileName}` : ''}
                {running || done ? ` — ${processed}/${ids.length} processed` : ''}
              </label>
              <div className="bulk-req-list">
                {ids.map(id => {
                  const st = statuses[id];
                  return (
                    <div key={id} className="bulk-req-row">
                      <span className="bulk-req-id">{id}</span>
                      {st?.state === 'pending' && <Spinner size="sm" className="icon-sm" />}
                      {st?.state === 'ok' && <span className="status-badge status-success"><CheckCircle className="icon-sm" />Requested</span>}
                      {st?.state === 'error' && <span className="status-badge status-error" title={st.message}><AlertCircle className="icon-sm" />Failed</span>}
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
        <div className="modal-footer">
          <Button variant="secondary" onClick={onClose} type="button" disabled={running}>{done ? 'Close' : 'Cancel'}</Button>
          <Button onClick={handleSubmit} disabled={running || done || !ids.length || !scope}>
            {running
              ? <><Spinner size="sm" className="icon-sm" />Requesting… {processed}/{ids.length}</>
              : <><Play className="icon-sm" />Request {ids.length || ''} source ID{ids.length !== 1 ? 's' : ''}</>}
          </Button>
        </div>
      </div>
    </div>
  );
}

// "Reports" picker — a searchable list of the reports available for a task (Overview, On-site
// instructions, …). Add a new report by adding one entry to the `reports` array at the call site; the
// dialog and its search pick it up automatically.
function ReportsModal({ isOpen, onClose, reports, onOpen }) {
  const [query, setQuery] = useState('');
  if (!isOpen) return null;
  const q = query.trim().toLowerCase();
  const filtered = (reports || []).filter(r => !q || r.name.toLowerCase().includes(q));
  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Reports</h2>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>
        <div className="modal-body">
          <div className="form-group">
            <input
              className="form-input"
              placeholder="Search reports by name…"
              value={query}
              onChange={e => setQuery(e.target.value)}
              autoFocus
            />
          </div>
          <div className="reports-list">
            {filtered.length === 0 ? (
              <div className="reports-empty">No reports match “{query}”.</div>
            ) : filtered.map(r => (
              <button key={r.name} type="button" className="reports-row" onClick={() => onOpen(r)}>
                <FileText className="icon-sm" />
                <span className="reports-row-text">
                  <span className="reports-row-name">{r.name}</span>
                  {r.description && <span className="reports-row-desc">{r.description}</span>}
                </span>
                <ChevronRight className="icon-sm reports-row-chev" />
              </button>
            ))}
          </div>
        </div>
        <div className="modal-footer">
          <Button variant="secondary" onClick={onClose} type="button">Close</Button>
        </div>
      </div>
    </div>
  );
}

// Simple Modal Components (to be extracted to separate files if desired)
// isDefault: render as a "Default Value" editor — a conversion with no source value. The Source Value
// field is hidden and stored as '', so the row becomes the default for that (record type, field).
function ActionConfirmModal({ isOpen, onClose, onConfirm, actionName }) {
  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content modal-sm" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Confirm Action</h2>
          <button className="modal-close" onClick={onClose}>
            <X className="icon-sm" />
          </button>
        </div>
        
        <div className="modal-body">
          <p>Are you sure you want to execute the action "<strong>{actionName}</strong>"?</p>
          <p className="text-muted">This action cannot be undone.</p>
        </div>
        
        <div className="modal-footer">
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={onConfirm}>
            <Play className="icon-sm" />
            Execute
          </Button>
        </div>
      </div>
    </div>
  );
}

// Mirror of the server's db.targetHotelIdProblem (ui-v2 sync) — keep the two in step. null when the Hotel Id
// is the project's hotel code, otherwise why not.
function targetHotelIdProblem(projectHotel, value) {
  if (!projectHotel) return 'This sync project has no hotel, so its Hotel Id cannot be checked. Link the project to its hotel first.';
  const code = String(projectHotel.hotelCode || '').trim();
  if (!code) return `${projectHotel.hotelName} has no Hotel Code, so its Hotel Id cannot be checked. Set the Hotel Code on the hotel first.`;
  const v = String(value == null ? '' : value).trim();
  if (v.toUpperCase() !== code.toUpperCase()) {
    return `Hotel Id must be ${code}, the Hotel Code of ${projectHotel.hotelName}`
      + (v ? ` — "${v}" would send this project's records to a different property.` : '.');
  }
  return null;
}

function PurgeConfirmModal({ isOpen, onClose, onConfirm }) {
  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content modal-sm" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Purge Records</h2>
          <button className="modal-close" onClick={onClose}>
            <X className="icon-sm" />
          </button>
        </div>
        <div className="modal-body">
          <p>This will permanently delete all S3 records for this task scope.</p>
          <p className="text-muted">This cannot be undone.</p>
        </div>
        <div className="modal-footer">
          <Button variant="secondary" onClick={onClose}>Cancel</Button>
          <Button variant="danger" onClick={onConfirm}>
            <Trash className="icon-sm" />
            Purge
          </Button>
        </div>
      </div>
    </div>
  );
}

export default TaskConfigurationPage;
